import { Injectable } from '@angular/core';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { DefinitionService } from '../definition/definition.service';
// import { Definition } from '../definition/definition';
import { FitType } from '../store/fit/fit-type.enum';
import { ShoppingCenterType } from '../identity/constant/shopping-center-type.enum';
// import { Observable, forkJoin, of } from 'rxjs';
// import { map } from 'rxjs/operators';
import * as _ from 'lodash';
import '../../../assets/fonts/SofiaSans-Bold-normal.js';
import "../../../assets/fonts/SofiaSans-Regular-normal.js";
import { round } from '../util/math-utils';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export interface StoreHeaderData {
  storeName: string;
  storeNumber?: string;
  bannerName?: string;
  fit?: string;
  address: string;
  cityStateZip: string;
  shoppingCenter?: string;
  shoppingCenterOwner?: string;
  shoppingCenterType?: string;
  logoUrl?: string;
}

export interface MetricItem {
  value: string;
  label: string;
  icon?: string;
  color?: string;
}

export interface AlertItem {
  title: string;
  description: string;
  isPositive: boolean;
}

export interface PerformanceItem {
  percentile: number;
  status: string;
  description: string;
  color: string;
}

export interface SalesPerformanceData {
  byVolume: PerformanceItem[];
  bySqft: PerformanceItem[];
  selectedView?: 'volume' | 'sqft'; // Which view is currently selected in UI
}

// Store Rank Table Data for PDF export
export interface StoreRankTableRow {
  storeName: string;
  storeSubtext?: string; // "Your Store (#15)", "Same Banner", etc.
  storeCode: string;
  status: string; // "Open" or "Closed"
  fit: string;
  salesPerSqft: string;
  totalSalesArea: string;
  totalSalesVolume: string;
  isCurrentStore?: boolean; // To highlight the current store
}

export interface StoreRankTableData {
  currentStoreRank: number;
  totalStores: number;
  rows: StoreRankTableRow[];
}

export interface DemographicData {
  ethnicity: {
    asian: number;
    hispanic: number;
    black: number;
    white: number;
    other: number;
  };
  population: {
    total: number;
    growth2yr: number;
    projected5yr: number;
    householdGrowth2yr?: number;
  };
  people: {
    averageAge: number;
    collegeEducated: number;
    householdSize: number;
  };
  income: {
    average: number;
    median: number;
  };
  regionText: string;
}

export interface BannerAnalysisData {
  bannerName: string;
  marketName: string;
  salesSqft?: PerformanceItem & { prefix?: string };
  storeCount?: PerformanceItem & { prefix?: string };
  salesVolume?: PerformanceItem & { prefix?: string };
}

export interface RatingItem {
  name: string;
  rating: string;
}

export interface AdditionalInsightsData {
  servicesCount: number;
  services: string[];
  amenities: string[];
  analystRatingDate?: string;
  storeRatings: RatingItem[][];
  parkingLotRatings: RatingItem[][];
  cotenantRatings: RatingItem[][];
}

export interface MapSearchPdfData {
  exportDate: string;
  exportTime: string;
  userDescription: string;
  mapImage?: string;
  storeLegend: string[];
  activeFilters: { category: string; value: string }[];
  showPolygonAnalysis: boolean;
  polygonAnalysis?: {
    areaSquareFeet?: string;
    areaAcres?: string;
    perimeterFeet?: string;
    perimeterMiles?: string;
    centerPointLat?: string;
    centerPointLong?: string;
  };
  stores: {
    name: string;
    banner: string;
    fit: string;
    address: string;
  }[];
}

export interface StoreAddPdfData {
  location: string;
  grocerName: string;
  sqft: string;
  sqftWarning?: string;
  weeklySalesRange: string;
  totalSalesProjection?: string;
  totalSalesProjectionLabel?: string;
  sqftProjection?: string;
  sqftProjectionLabel?: string;
  closestSameBanner?: string;
  closestCompetitor?: string;
  futureOpenings?: string;
  competitiveData: {
    name?: string;
    banner?: string;
    value?: string;
    count?: number;
    totalCount?: number;
  }[];
  demographics: DemographicData;
  mapImage?: string;
  keyIndicator?: string;
  regionText: string;
  competitiveRegionText?: string;
}

export interface RankingTablePdfRow {
  name: string;
  storeNumber?: string;
  address?: string;
  annualSales: string;
  nationalRank: string;
  stateRank: string;
  countyRank: string;
  msaRank: string;
  miles: string;
  minutes: string;
  fit?: string;        // Store FIT Category (GRO-711)
  storeSize?: string;  // Store Size, in the area named by storeSizeLabel (GRO-711, GRO-911)
  distance?: string;   // Distance (Miles) from the subject store (GRO-711)
  isSelected?: boolean;
}

export interface RankingTablePdfData {
  storeName: string;
  storeAddress: string;
  storeStatus?: string;
  estSalesVolume?: string;
  estSalesSqft?: string;
  fitCategory?: string;
  milesOption?: string;
  minutesOption?: string;
  salesType?: string;
  // GRO-911: 'Total SQFT' or 'Sales Area SQFT', per the user's Sales SQFT preference
  storeSizeLabel?: string;
  filters?: {
    salesVolume?: string;
    states?: string;
    cbsa?: string;
    fit?: string;
    sqft?: string;
  };
  tableRows: RankingTablePdfRow[];
}

export interface ScatterChartPdfRow {
  rank: number;
  locationName: string;
  fit: string;
  status: string;
  salesPerSqft: string;
  totalArea: string;
  totalSalesVolume: string;
  isSelected?: boolean;
}

export interface ScatterChartPdfData {
  storeName: string;
  storeAddress?: string;
  storeStatus?: string;
  estSalesVolume?: string;
  estSalesSqft?: string;
  fitCategory?: string;
  chartTitle: string;
  keyIndicator: string;
  comparisonType: string;
  filters?: {
    driveTime?: string;
    region?: string;
    storeType?: string;
    miles?: string;
    minutes?: string;
  };
  tableRows: ScatterChartPdfRow[];
}

// Image assets interface
export interface PdfImageAssets {
  patternSegment?: string;    // Pattern line image base64
  retailstatLogo?: string;    // Footer logo base64
  vectorIcons?: {             // Demographic card icons
    growth?: string;          // Vector.png
    population?: string;      // Vector (1).png
    projected?: string;       // Vector (2).png
    age?: string;             // Vector (3).png
    educated?: string;        // Vector (4).png
    household?: string;       // Vector (5).png
    income1?: string;         // Vector (6).png
    income2?: string;         // Vector (8).png
  };
  metricIcons?: string[];     // Critical insights metric icons
  locationIcon?: string;   // Location marker icon for store header
  shoppingBagIcon?: string;   // Shopping bag icon for store header
  storeAlterIcon?: string;   // Store icon for store header
  dollarIcon?: string;     // Dollar sign icon for income cards
  homeIcon?: string;       // Home icon for income cards
}

// ============================================================================
// PDF SERVICE
// ============================================================================

@Injectable({
  providedIn: 'root'
})
export class DynamicPdfService {
  private imageAssets: PdfImageAssets = {};
  // Page dimensions (Letter size: 612x792 pixels = 215.9x279.4 mm)
  private readonly PAGE_WIDTH = 215.9;
  private readonly PAGE_HEIGHT = 279.4;
  private readonly PX_TO_MM = 215.9 / 612;

  // Margins - compact for more content space
  private readonly margins = {
    top: 5,
    right: 7,
    bottom: 5,
    left: 6
  };

  // Colors from SCSS files - exact matches
  private readonly colors = {
    // Primary colors
    primary: '#2D5D9B',
    text: '#0A0A0A',
    textLight: '#4A5565',
    textMuted: '#6C6A61',
    border: '#000000',
    borderLight: '#DBDBDB',

    // Status colors
    success: '#32CD32',
    danger: '#F44437',

    // Demographic card colors (from store-detail-pdf-second-page.component.scss)
    ethnicityPurple: '#853399',
    populationBlue: '#3D7BC9',
    peopleBlue: '#0033A2',
    incomePurple: '#68478B',

    // Map Search PDF colors (from map-search-pdf-page.component.scss)
    headerDark: '#0F1B4D',
    descBg: '#EFF6FF',
    descBorder: '#2563EB',
    tableGray: '#E9E9E9',
    metricBlue: '#4779F5',
    filterBorder: '#00000033',

    // Footer
    footerBrand: '#853694',
    footerText: '#04091D'
  };

  constructor(private definitionService: DefinitionService) {}

  // Helper method to render SVG to high-resolution PNG
  private renderSvgToBase64(svgContent: string, width: number, height: number): Promise<string> {
    return new Promise((resolve) => {
      // Use 8x scale for ultra-crisp rendering in PDFs
      const scale = 8;
      const canvas = document.createElement('canvas');
      canvas.width = width * scale;
      canvas.height = height * scale;
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        resolve('');
        return;
      }

      // Disable smoothing for crisp pixel-perfect rendering
      ctx.imageSmoothingEnabled = false;

      const img = new Image();
      // Add width and height to SVG for proper scaling
      const svgWithSize = svgContent.replace(
        '<svg ',
        `<svg width="${width * scale}" height="${height * scale}" `
      );
      const svgBlob = new Blob([svgWithSize], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(svgBlob);

      img.onload = () => {
        ctx.drawImage(img, 0, 0, width * scale, height * scale);
        URL.revokeObjectURL(url);
        resolve(canvas.toDataURL('image/png', 1.0));
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve('');
      };
      img.src = url;
    });
  }

  // Create shopping bag icon as SVG - high resolution
  private createShoppingBagIconSvg(): Promise<string> {
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512"><path fill="#000000" d="M448 224v-32a32 32 0 0 0-32-32h-96V96a96 96 0 0 0-192 0v64H32a32 32 0 0 0-32 32v256a32 32 0 0 0 32 32h128V256a32 32 0 0 1 32-32zM176 96a48 48 0 0 1 96 0v64h-96zm368 160H224a32 32 0 0 0-32 32v192a32 32 0 0 0 32 32h320a32 32 0 0 0 32-32V288a32 32 0 0 0-32-32zm-64 96a96.12 96.12 0 0 1-108.63 95.19C323 441 288 397.45 288 348.66V328a8 8 0 0 1 8-8h16a8 8 0 0 1 8 8v21.16c0 33.41 24.37 63.3 57.62 66.53A64.07 64.07 0 0 0 448 352v-24a8 8 0 0 1 8-8h16a8 8 0 0 1 8 8z"/></svg>`;
    return this.renderSvgToBase64(svg, 48, 48);
  }

  // Create store icon as SVG - high resolution
  private createStoreAlterIconSvg(): Promise<string> {
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512"><path fill="#000000" d="M320 384H128V224H64v256c0 17.7 14.3 32 32 32h256c17.7 0 32-14.3 32-32V224h-64v160zm314.6-241.8l-85.3-128c-6-8.9-16-14.2-26.7-14.2H117.4c-10.7 0-20.7 5.3-26.6 14.2l-85.3 128c-14.2 21.3 1 49.8 26.6 49.8H608c25.5 0 40.7-28.5 26.6-49.8zM512 496c0 8.8 7.2 16 16 16h32c8.8 0 16-7.2 16-16V224h-64v272z"/></svg>`;
    return this.renderSvgToBase64(svg, 48, 48);
  }

  // Create location marker icon as SVG - high resolution
  private createlocationIconSvg(): Promise<string> {
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512"><path fill="#000000" d="M172.268 501.67C26.97 291.031 0 269.413 0 192 0 85.961 85.961 0 192 0s192 85.961 192 192c0 77.413-26.97 99.031-172.268 309.67-9.535 13.774-29.93 13.773-39.464 0zM192 272c44.183 0 80-35.817 80-80s-35.817-80-80-80-80 35.817-80 80 35.817 80 80 80z"/></svg>`;
    return this.renderSvgToBase64(svg, 48, 48);
  }

  // Create dollar sign icon as SVG - high resolution
  private createDollarIconSvg(): Promise<string> {
    const svg = `<svg role="img" aria-hidden="true" focusable="false" data-prefix="fas" data-icon="dollar-sign" class="svg-inline--fa fa-dollar-sign fa-w-9 fa-2x" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 288 512"><path fill="#eceaeaff" d="M209.2 233.4l-108-31.6C88.7 198.2 80 186.5 80 173.5c0-16.3 13.2-29.5 29.5-29.5h66.3c12.2 0 24.2 3.7 34.2 10.5 6.1 4.1 14.3 3.1 19.5-2l34.8-34c7.1-6.9 6.1-18.4-1.8-24.5C238 74.8 207.4 64.1 176 64V16c0-8.8-7.2-16-16-16h-32c-8.8 0-16 7.2-16 16v48h-2.5C45.8 64-5.4 118.7.5 183.6c4.2 46.1 39.4 83.6 83.8 96.6l102.5 30c12.5 3.7 21.2 15.3 21.2 28.3 0 16.3-13.2 29.5-29.5 29.5h-66.3C100 368 88 364.3 78 357.5c-6.1-4.1-14.3-3.1-19.5 2l-34.8 34c-7.1 6.9-6.1 18.4 1.8 24.5 24.5 19.2 55.1 29.9 86.5 30v48c0 8.8 7.2 16 16 16h32c8.8 0 16-7.2 16-16v-48.2c46.6-.9 90.3-28.6 105.7-72.7 21.5-61.6-14.6-124.8-72.5-141.7z"></path></svg>`;
    return this.renderSvgToBase64(svg, 24, 24);
  }

  // Create growth/history icon as SVG
  private createGrowthIconSvg(): Promise<string> {
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path fill="#eceaeaff" d="M75 75L41 41C25.9 25.9 0 36.6 0 57.9V168c0 13.3 10.7 24 24 24H134.1c21.4 0 32.1-25.9 17-41l-30.8-30.8C155 85.5 203 64 256 64c106 0 192 86 192 192s-86 192-192 192c-40.8 0-78.6-12.7-109.7-34.4c-14.5-10.1-34.4-6.6-44.6 7.9s-6.6 34.4 7.9 44.6C151.2 495 201.7 512 256 512c141.4 0 256-114.6 256-256S397.4 0 256 0C185.3 0 121.3 28.7 75 75zm181 53c-13.3 0-24 10.7-24 24V256c0 6.4 2.5 12.5 7 17l72 72c9.4 9.4 24.6 9.4 33.9 0s9.4-24.6 0-33.9l-65-65V152c0-13.3-10.7-24-24-24z"/></svg>`;
    return this.renderSvgToBase64(svg, 24, 24);
  }

  // Create population/users icon as SVG
  private createPopulationIconSvg(): Promise<string> {
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512"><path fill="#eceaeaff" d="M144 0a80 80 0 1 1 0 160A80 80 0 1 1 144 0zM512 0a80 80 0 1 1 0 160A80 80 0 1 1 512 0zM0 298.7C0 239.8 47.8 192 106.7 192h42.7c15.9 0 31 3.5 44.6 9.7c-1.3 7.2-1.9 14.7-1.9 22.3c0 38.2 16.8 72.5 43.3 96c-.2 0-.4 0-.7 0H21.3C9.6 320 0 310.4 0 298.7zM405.3 320c-.2 0-.4 0-.7 0c26.6-23.5 43.3-57.8 43.3-96c0-7.6-.7-15-1.9-22.3c13.6-6.3 28.7-9.7 44.6-9.7h42.7C592.2 192 640 239.8 640 298.7c0 11.8-9.6 21.3-21.3 21.3H405.3zM224 224a96 96 0 1 1 192 0 96 96 0 1 1 -192 0zM128 485.3C128 411.7 187.7 352 261.3 352H378.7C452.3 352 512 411.7 512 485.3c0 14.7-11.9 26.7-26.7 26.7H154.7c-14.7 0-26.7-11.9-26.7-26.7z"/></svg>`;
    return this.renderSvgToBase64(svg, 24, 24);
  }

  // Create projected/chart icon as SVG
  private createProjectedIconSvg(): Promise<string> {
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path fill="#eceaeaff" d="M64 64c0-17.7-14.3-32-32-32S0 46.3 0 64V400c0 44.2 35.8 80 80 80H480c17.7 0 32-14.3 32-32s-14.3-32-32-32H80c-8.8 0-16-7.2-16-16V64zm406.6 86.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L320 210.7l-57.4-57.4c-12.5-12.5-32.8-12.5-45.3 0l-112 112c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L240 221.3l57.4 57.4c12.5 12.5 32.8 12.5 45.3 0l128-128z"/></svg>`;
    return this.renderSvgToBase64(svg, 24, 24);
  }

  // Create age/clock icon as SVG
  private createAgeIconSvg(): Promise<string> {
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path fill="#eceaeaff" d="M464 256A208 208 0 1 1 48 256a208 208 0 1 1 416 0zM0 256a256 256 0 1 0 512 0A256 256 0 1 0 0 256zM232 120V256c0 8 4 15.5 10.7 20l96 64c11 7.4 25.9 4.4 33.3-6.7s4.4-25.9-6.7-33.3L280 243.2V120c0-13.3-10.7-24-24-24s-24 10.7-24 24z"/></svg>`;
    return this.renderSvgToBase64(svg, 24, 24);
  }

  // Create education/graduation cap icon as SVG
  private createEducationIconSvg(): Promise<string> {
    const svg = `<svg role="img" aria-hidden="true" focusable="false" data-prefix="fas" data-icon="graduation-cap" class="svg-inline--fa fa-graduation-cap fa-w-20 fa-2x" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512"><path fill="#eceaeaff" d="M622.34 153.2L343.4 67.5c-15.2-4.67-31.6-4.67-46.79 0L17.66 153.2c-23.54 7.23-23.54 38.36 0 45.59l48.63 14.94c-10.67 13.19-17.23 29.28-17.88 46.9C38.78 266.15 32 276.11 32 288c0 10.78 5.68 19.85 13.86 25.65L20.33 428.53C18.11 438.52 25.71 448 35.94 448h56.11c10.24 0 17.84-9.48 15.62-19.47L82.14 313.65C90.32 307.85 96 298.78 96 288c0-11.57-6.47-21.25-15.66-26.87.76-15.02 8.44-28.3 20.69-36.72L296.6 284.5c9.06 2.78 26.44 6.25 46.79 0l278.95-85.7c23.55-7.24 23.55-38.36 0-45.6zM352.79 315.09c-28.53 8.76-52.84 3.92-65.59 0l-145.02-44.55L128 384c0 35.35 85.96 64 192 64s192-28.65 192-64l-14.18-113.47-145.03 44.56z"></path></svg>`;
    return this.renderSvgToBase64(svg, 20, 20);
  }

  // Create household/home icon as SVG (different from income home)
  private createHouseholdIconSvg(): Promise<string> {
    const svg = `<svg role="img" aria-hidden="true" focusable="false" data-prefix="fas" data-icon="home" class="svg-inline--fa fa-home fa-w-18 fa-2x" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512"><path fill="#eceaeaff" d="M280.37 148.26L96 300.11V464a16 16 0 0 0 16 16l112.06-.29a16 16 0 0 0 15.92-16V368a16 16 0 0 1 16-16h64a16 16 0 0 1 16 16v95.64a16 16 0 0 0 16 16.05L464 480a16 16 0 0 0 16-16V300L295.67 148.26a12.19 12.19 0 0 0-15.3 0zM571.6 251.47L488 182.56V44.05a12 12 0 0 0-12-12h-56a12 12 0 0 0-12 12v72.61L318.47 43a48 48 0 0 0-61 0L4.34 251.47a12 12 0 0 0-1.6 16.9l25.5 31A12 12 0 0 0 45.15 301l235.22-193.74a12.19 12.19 0 0 1 15.3 0L530.9 301a12 12 0 0 0 16.9-1.6l25.5-31a12 12 0 0 0-1.7-16.93z"></path></svg>`;
    return this.renderSvgToBase64(svg, 20, 20);
  }

  // Create home icon as SVG - high resolution
  private createHomeIconSvg(): Promise<string> {
    const svg = `<svg role="img" aria-hidden="true" focusable="false" data-prefix="fas" data-icon="home" class="svg-inline--fa fa-home fa-w-18" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512"><path fill="#ffff" d="M280.37 148.26L96 300.11V464a16 16 0 0 0 16 16l112.06-.29a16 16 0 0 0 15.92-16V368a16 16 0 0 1 16-16h64a16 16 0 0 1 16 16v95.64a16 16 0 0 0 16 16.05L464 480a16 16 0 0 0 16-16V300L295.67 148.26a12.19 12.19 0 0 0-15.3 0zM571.6 251.47L488 182.56V44.05a12 12 0 0 0-12-12h-56a12 12 0 0 0-12 12v72.61L318.47 43a48 48 0 0 0-61 0L4.34 251.47a12 12 0 0 0-1.6 16.9l25.5 31A12 12 0 0 0 45.15 301l235.22-193.74a12.19 12.19 0 0 1 15.3 0L530.9 301a12 12 0 0 0 16.9-1.6l25.5-31a12 12 0 0 0-1.7-16.93z"></path></svg>`;
    return this.renderSvgToBase64(svg, 16, 16);
  }

  // Create services icon as SVG
  private createServicesIconSvg(): Promise<string> {
    const svg = `<svg role="img" aria-hidden="true" focusable="false" data-prefix="fas" data-icon="bell" class="svg-inline--fa fa-bell fa-w-14" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path fill="currentColor" d="M224 512c35.32 0 63.97-28.65 63.97-64H160.03c0 35.35 28.65 64 63.97 64zm215.39-149.71c-19.32-20.76-55.47-51.99-55.47-154.29 0-77.7-54.48-139.9-127.94-155.16V32c0-17.67-14.32-32-31.98-32s-31.98 14.33-31.98 32v20.84C118.56 68.1 64.08 130.3 64.08 208c0 102.3-36.15 133.53-55.47 154.29-6 6.45-8.66 14.16-8.61 21.71.11 16.4 12.98 32 32.1 32h383.8c19.12 0 32-15.6 32.1-32 .05-7.55-2.61-15.27-8.61-21.71z"></path></svg>`;
    return this.renderSvgToBase64(svg, 24, 24);
  }

  // Create ratings icon as SVG
  private createRatingsIconSvg(): Promise<string> {
    const svg = `<svg role="img" aria-hidden="true" focusable="false" data-prefix="fas" data-icon="star-half-alt" class="svg-inline--fa fa-star-half-alt fa-w-17" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 536 512"><path fill="currentColor" d="M508.55 171.51L362.18 150.2 296.77 17.81C290.89 5.98 279.42 0 267.95 0c-11.4 0-22.79 5.9-28.69 17.81l-65.43 132.38-146.38 21.29c-26.25 3.8-36.77 36.09-17.74 54.59l105.89 103-25.06 145.48C86.98 495.33 103.57 512 122.15 512c4.93 0 10-1.17 14.87-3.75l130.95-68.68 130.94 68.7c4.86 2.55 9.92 3.71 14.83 3.71 18.6 0 35.22-16.61 31.66-37.4l-25.03-145.49 105.91-102.98c19.04-18.5 8.52-50.8-17.73-54.6zm-121.74 123.2l-18.12 17.62 4.28 24.88 19.52 113.45-102.13-53.59-22.38-11.74.03-317.19 51.03 103.29 11.18 22.63 25.01 3.64 114.23 16.63-82.65 80.38z"></path></svg>`;
    return this.renderSvgToBase64(svg, 24, 24);
  }

private async loadImageAsBase64(url: string, targetWidth?: number): Promise<string> {
    console.log("🚀 ~ DynamicPdfService ~ loadImageAsBase64 ~ targetWidth:", targetWidth)
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'Anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');

        // Calculate dimensions
        let width = img.width;
        let height = img.height;

        // If targetWidth is specified, scale down proportionally
        if (targetWidth && width > targetWidth) {
          height = (height * targetWidth) / width;
          width = targetWidth;
        }

        // Use the actual dimensions (or scaled down if needed)
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (ctx) {
          // Enable high-quality image scaling
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';

          // Draw at actual size
          ctx.drawImage(img, 0, 0, width, height);
        }

        // Use high quality PNG export
        resolve(canvas.toDataURL('image/png', 1.0));
      };
      img.onerror = () => resolve('');
      img.src = url;
    });
  }

  // Pre-load all required image assets
  private footerLogo: string = '';

  async preloadImages(): Promise<void> {
    const baseUrl = window.location.origin;
    const [
      patternSegment, retailstatLogo, footerLogo,
      metric1, metric2, metric3, shoppingBagIcon, locationIcon, storeAlterIcon, dollarIcon, homeIcon,
      growthIcon, populationIcon, projectedIcon, ageIcon, educationIcon, householdIcon,
      servicesIcon, ratingsIcon
    ] = await Promise.all([
      this.loadImageAsBase64('https://api.builder.io/api/v1/image/assets/TEMP/89cf6a021f0e4a02b2299f99f1233c6c345c9fd4?width=612'),
      this.loadImageAsBase64(`${baseUrl}/assets/images/rs_logo_blue_white_inline_transparent_cropped.png`),
      this.loadImageAsBase64(`${baseUrl}/assets/images/resized-retailstat-icon.png`),
      this.loadImageAsBase64(`${baseUrl}/assets/images/FreeSample-Vectorizer-io-389687af1c7ca0c4fa762732ed5d95852f9e0f5e.svg`),
      this.loadImageAsBase64(`${baseUrl}/assets/images/FreeSample-Vectorizer-io-98d91d54b0948e6a426925ccfd1d76ab3b13a652.svg`),
      this.loadImageAsBase64(`${baseUrl}/assets/images/FreeSample-Vectorizer-io-e8f4b7b33da0101f2fbc50f99383950056b6d378.svg`),
      this.createShoppingBagIconSvg(),
      this.createlocationIconSvg(),
      this.createStoreAlterIconSvg(),
      this.createDollarIconSvg(),
      this.createHomeIconSvg(),
      this.createGrowthIconSvg(),
      this.createPopulationIconSvg(),
      this.createProjectedIconSvg(),
      this.createAgeIconSvg(),
      this.createEducationIconSvg(),
      this.createHouseholdIconSvg(),
      this.createServicesIconSvg(),
      this.createRatingsIconSvg()
    ]);

    this.imageAssets = {
      patternSegment,
      retailstatLogo,
      vectorIcons: { growth: growthIcon, population: populationIcon, projected: projectedIcon, age: ageIcon, educated: educationIcon, household: householdIcon },
      metricIcons: [metric1, metric2, metric3],
      shoppingBagIcon,
      locationIcon,
      storeAlterIcon,
      dollarIcon,
      homeIcon
    };

    // Store additional insight icons
    (this.imageAssets as any).servicesIcon = servicesIcon;
    (this.imageAssets as any).ratingsIcon = ratingsIcon;

    this.footerLogo = footerLogo;
  }

  // ============================================================================
  // UTILITY METHODS
  // ============================================================================

  private px(pixels: number): number {
    return pixels * this.PX_TO_MM;
  }

  private getContentWidth(): number {
    return this.PAGE_WIDTH - this.margins.left - this.margins.right;
  }

  private convertToCamelCase(text: string): string {
    if (!text) return text;
    return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
  }

  private formatFitCategory(fit: string): string {
    if (!fit) return fit;
    return fit.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
  }

  private hexToRgb(color: string): [number, number, number] {
    if (!color || typeof color !== 'string') {
      return [153, 153, 153]; // Default gray if no color
    }

    const trimmedColor = color.trim();

    // Handle rgb(r g b) format with SPACES - CSS Color Level 4 syntax (chroma.js uses this!)
    const rgbSpaceMatch = trimmedColor.match(/^rgb\s*\(\s*([\d.]+)\s+([\d.]+)\s+([\d.]+)\s*\)/i);
    if (rgbSpaceMatch) {
      return [
        Math.round(parseFloat(rgbSpaceMatch[1])),
        Math.round(parseFloat(rgbSpaceMatch[2])),
        Math.round(parseFloat(rgbSpaceMatch[3]))
      ];
    }

    // Handle rgb(r, g, b) format with COMMAS - old CSS syntax
    const rgbCommaMatch = trimmedColor.match(/^rgb\s*\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)\s*\)/i);
    if (rgbCommaMatch) {
      return [
        Math.round(parseFloat(rgbCommaMatch[1])),
        Math.round(parseFloat(rgbCommaMatch[2])),
        Math.round(parseFloat(rgbCommaMatch[3]))
      ];
    }

    // Handle rgba with spaces: rgba(r g b / a) - CSS Color Level 4
    const rgbaSpaceMatch = trimmedColor.match(/^rgba?\s*\(\s*([\d.]+)\s+([\d.]+)\s+([\d.]+)\s*(?:\/\s*[\d.]+)?\s*\)/i);
    if (rgbaSpaceMatch) {
      return [
        Math.round(parseFloat(rgbaSpaceMatch[1])),
        Math.round(parseFloat(rgbaSpaceMatch[2])),
        Math.round(parseFloat(rgbaSpaceMatch[3]))
      ];
    }

    // Handle rgba(r, g, b, a) format with commas
    const rgbaCommaMatch = trimmedColor.match(/^rgba\s*\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*[\d.]+\s*\)/i);
    if (rgbaCommaMatch) {
      return [
        Math.round(parseFloat(rgbaCommaMatch[1])),
        Math.round(parseFloat(rgbaCommaMatch[2])),
        Math.round(parseFloat(rgbaCommaMatch[3]))
      ];
    }

    // Handle hex format (#RGB, #RRGGBB, #RRGGBBAA)
    let hex = trimmedColor;
    if (hex.startsWith('#')) {
      if (hex.length === 9) hex = hex.substring(0, 7);
      if (hex.length === 4) {
        hex = '#' + hex[1] + hex[1] + hex[2] + hex[2] + hex[3] + hex[3];
      }
      const hexResult = /^#([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      if (hexResult) {
        return [parseInt(hexResult[1], 16), parseInt(hexResult[2], 16), parseInt(hexResult[3], 16)];
      }
    }

    // Handle named colors
    const namedColors: Record<string, [number, number, number]> = {
      'red': [255, 0, 0],
      'green': [0, 128, 0],
      'limegreen': [50, 205, 50],
      'orange': [255, 165, 0],
      'yellow': [255, 255, 0],
      'blue': [0, 0, 255],
      'gray': [128, 128, 128],
      'grey': [128, 128, 128]
    };
    if (namedColors[trimmedColor.toLowerCase()]) {
      return namedColors[trimmedColor.toLowerCase()];
    }

    return [153, 153, 153];
  }

  private drawLine(doc: jsPDF, x1: number, y1: number, x2: number, y2: number, color: string, width: number = 0.3): void {
    doc.setDrawColor(...this.hexToRgb(color));
    doc.setLineWidth(width);
    doc.line(x1, y1, x2, y2);
  }

  private drawRect(doc: jsPDF, x: number, y: number, w: number, h: number, fillColor: string, strokeColor?: string): void {
    doc.setFillColor(...this.hexToRgb(fillColor));
    if (strokeColor) {
      doc.setDrawColor(...this.hexToRgb(strokeColor));
      doc.setLineWidth(0.3);
      doc.rect(x, y, w, h, 'FD');
    } else {
      doc.rect(x, y, w, h, 'F');
    }
  }

  private drawRoundedRect(doc: jsPDF, x: number, y: number, w: number, h: number, radius: number, fillColor: string): void {
    doc.setFillColor(...this.hexToRgb(fillColor));
    // Draw rounded rectangle using arcs and lines
    doc.roundedRect(x, y, w, h, radius, radius, 'F');
  }

  private drawProgressRing(doc: jsPDF, cx: number, cy: number, radius: number, percentile: number, color: string): void {
    // Parse the color first to get RGB values
    const rgb = this.hexToRgb(color);

    // Background circle (light gray)
    doc.setDrawColor(224, 224, 224);
    doc.setLineWidth(1.5);
    doc.circle(cx, cy, radius, 'S');

    // Progress arc with color - draw even for small percentiles
    const effectivePercentile = Math.max(0, Math.min(100, percentile || 0));
    if (effectivePercentile > 0) {
      const startAngle = -90;
      const endAngle = startAngle + (360 * effectivePercentile / 100);
      // Use more segments for smoother arc
      const segments = Math.max(6, Math.floor(effectivePercentile));
      const step = (endAngle - startAngle) / segments;

      for (let i = 0; i < segments; i++) {
        const a1 = (startAngle + i * step) * Math.PI / 180;
        const a2 = (startAngle + (i + 1) * step) * Math.PI / 180;

        // Set color before each line segment to ensure it's applied
        doc.setDrawColor(rgb[0], rgb[1], rgb[2]);
        doc.setLineWidth(1.5);

        doc.line(
          cx + radius * Math.cos(a1),
          cy + radius * Math.sin(a1),
          cx + radius * Math.cos(a2),
          cy + radius * Math.sin(a2)
        );
      }
    }
  }

  // Helper method to render multi-line text within cell boundaries
  private drawMultiLineText(doc: jsPDF, text: string, x: number, y: number, maxWidth: number, lineHeight: number = 4): void {
    if (!text) return;

    const lines = doc.splitTextToSize(text, maxWidth);
    lines.forEach((line: string, index: number) => {
      doc.text(line, x, y + (index * lineHeight));
    });
  }

  // ============================================================================
  // STORE DETAIL PDF - FIRST PAGE
  // ============================================================================

  // private drawHeaderPatternLine(doc: jsPDF, y: number): number {
  //   const h = this.px(15);
  //   // Pattern line at top - two-tone stripe
  //   if (this.imageAssets.patternSegment) {
  //     try {
  //       doc.addImage(this.imageAssets.patternSegment, 'PNG', 0, y, this.PAGE_WIDTH / 2, h);
  //       doc.addImage(this.imageAssets.patternSegment, 'PNG', this.PAGE_WIDTH / 2, y, this.PAGE_WIDTH / 2, h);
  //     } catch {
  //       this.drawRect(doc, 0, y, this.PAGE_WIDTH / 2, h, this.colors.primary);
  //       this.drawRect(doc, this.PAGE_WIDTH / 2, y, this.PAGE_WIDTH / 2, h, '#009688');
  //     }
  //   } else {
  //     this.drawRect(doc, 0, y, this.PAGE_WIDTH / 2, h, this.colors.primary);
  //     this.drawRect(doc, this.PAGE_WIDTH / 2, y, this.PAGE_WIDTH / 2, h, '#009688');
  //   }
  //   return y + h + 2;
  // }

  // Pattern line at bottom of page (position: top: 777px from HTML = near bottom)
  private drawPatternLine(doc: jsPDF, customY?: number): void {
    const pxToMm = (px: number) => px * 0.3528;
    const h = pxToMm(15); // 15px height
    const y = customY !== undefined ? customY : this.PAGE_HEIGHT - h; // Default to bottom of page

    if (this.imageAssets.patternSegment) {
      try {
        doc.addImage(this.imageAssets.patternSegment, 'PNG', 0, y, this.PAGE_WIDTH / 2, h);
        doc.addImage(this.imageAssets.patternSegment, 'PNG', this.PAGE_WIDTH / 2, y, this.PAGE_WIDTH / 2, h);
      } catch {
        // Fallback: Draw colored rectangles
        this.drawRect(doc, 0, y, this.PAGE_WIDTH / 2, h, this.colors.primary);
        this.drawRect(doc, this.PAGE_WIDTH / 2, y, this.PAGE_WIDTH / 2, h, '#009688');
      }
    } else {
      // Fallback: Draw colored rectangles
      this.drawRect(doc, 0, y, this.PAGE_WIDTH / 2, h, this.colors.primary);
      this.drawRect(doc, this.PAGE_WIDTH / 2, y, this.PAGE_WIDTH / 2, h, '#009688');
    }
  }

  private async drawStoreHeader(doc: jsPDF, data: StoreHeaderData & { fitDisplayName?: string; shoppingCenterTypeDisplayName?: string }, y: number): Promise<number> {
    const x = this.margins.left;
    const contentWidth = this.getContentWidth();
    const colWidth = contentWidth / 3;
    // const logoHeight = 12;
    const iconSpacing = 12;

    // Three-column header layout with proper spacing
    // Column 1: Banner logo or store icon + Store name and banner
    let textStartX = x + 18; // Default for square logos

    if (data.logoUrl) {
      try {
        const logoImg = await this.loadImageAsBase64(data.logoUrl);
       if (logoImg) {
          const imgProps = doc.getImageProperties(logoImg);
          const aspectRatio = imgProps.width / imgProps.height;

          let logoWidth, logoHeight;
          if (aspectRatio >= 0.8 && aspectRatio <= 1.2) {
            // Square logo: set width to 15
            logoWidth = 15;
            logoHeight = 15;
            textStartX = x + 18;
          } else {
            // Rectangle logo: use original logic
            logoWidth = 23;
            logoHeight = (imgProps.height * logoWidth) / imgProps.width;
            textStartX = x + 26; // 23px width + 3px spacing
          }

          doc.addImage(logoImg, 'PNG', x - 2, y+2 , logoWidth, logoHeight);
        }
        else {
          throw new Error('Logo failed to load');
        }
      } catch (error) {
        // Fallback: Draw store icon with proper scaling
        if (this.imageAssets.storeAlterIcon) {
          const imgProps = doc.getImageProperties(this.imageAssets.storeAlterIcon);
          const iconSize = 15;
          const aspectRatio = imgProps.width / imgProps.height;
          const iconWidth = iconSize * aspectRatio;
          doc.addImage(this.imageAssets.storeAlterIcon, 'PNG', x, y, iconWidth, iconSize);
          textStartX = x + iconWidth + 1;
        }

      }
    } else {
      // Draw store icon with proper scaling
      if (this.imageAssets.storeAlterIcon) {
        const imgProps = doc.getImageProperties(this.imageAssets.storeAlterIcon);
        const iconSize = 15;
        const aspectRatio = imgProps.width / imgProps.height;
        const iconWidth = iconSize * aspectRatio;
        doc.addImage(this.imageAssets.storeAlterIcon, 'PNG', x, y, iconWidth, iconSize);
        textStartX = x + iconWidth + 1;
      }

    }

    doc.setFontSize(13);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...this.hexToRgb(this.colors.text));
    const storeName = data.storeNumber ? `${data.storeName} (${data.storeNumber})` : data.storeName;
    const col1TextWidth = colWidth - iconSpacing - 6;
    const storeLines = doc.splitTextToSize(storeName, col1TextWidth);

    // Draw store name with wrapping if needed
    let col1TextY = y + 5;
    const maxStoreLines = Math.min(storeLines.length, 2);
    for (let i = 0; i < maxStoreLines; i++) {
      doc.text(storeLines[i], textStartX, col1TextY);
      col1TextY += 4.5;
    }

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...this.hexToRgb(this.colors.textLight));
    const fitDisplay = this.formatFitCategory(data.fit) || 'No FIT';
    const bannerTextY = maxStoreLines > 1 ? col1TextY : y + 10;
    doc.text(`${data.bannerName || 'No Banner'} `, textStartX, bannerTextY);
    doc.text(`${data.fitDisplayName || fitDisplay} `, textStartX, bannerTextY + 5);

    // Column 2: Map marker icon + Address
    const col2X = x + colWidth+10;
    if (this.imageAssets.locationIcon) {
      const imgProps = doc.getImageProperties(this.imageAssets.locationIcon);
      const iconSize = 12;
      const aspectRatio = imgProps.width / imgProps.height;
      const iconWidth = iconSize * aspectRatio;
      doc.addImage(this.imageAssets.locationIcon, 'PNG', col2X, y, iconWidth, iconSize);
    }

    doc.setFontSize(13);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...this.hexToRgb(this.colors.text));
    const col2TextWidth = colWidth - iconSpacing - 6;
    const addrLines = doc.splitTextToSize(data.address || '', col2TextWidth);

    // Draw address with wrapping if needed
    let col2TextY = y + 5;
    const maxAddrLines = Math.min(addrLines.length, 2);
    for (let i = 0; i < maxAddrLines; i++) {
      doc.text(addrLines[i], col2X + 15, col2TextY);
      col2TextY += 4.5;
    }

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...this.hexToRgb(this.colors.textLight));
    const cityStateZipY = maxAddrLines > 1 ? col2TextY : y + 10;
    doc.text(data.cityStateZip || '', col2X + 15, cityStateZipY);

    // Column 3: Shopping bag icon + Shopping Center
    const col3X = x + colWidth * 2  +10 ;
    // Use shopping bag icon or fallback to drawn shape
    if (this.imageAssets.shoppingBagIcon) {
      try {
        doc.addImage(this.imageAssets.shoppingBagIcon, 'PNG', col3X, y, 12, 12);
      } catch (error) {
        // Fallback: Draw shopping bag icon
        doc.setFillColor(...this.hexToRgb(this.colors.text));
        doc.rect(col3X + 2, y + 3, 8, 8, 'F');
        doc.setDrawColor(...this.hexToRgb(this.colors.text));
        doc.setLineWidth(0.8);
        doc.line(col3X + 3.5, y + 3, col3X + 3.5, y + 1);
        doc.line(col3X + 7.5, y + 3, col3X + 7.5, y + 1);
        doc.line(col3X + 4, y + 1, col3X + 7.5, y + 1);
      }
    } else {
      // Fallback: Draw shopping bag icon
      doc.setFillColor(...this.hexToRgb(this.colors.text));
      doc.rect(col3X + 2, y + 3, 8, 8, 'F');
      doc.setDrawColor(...this.hexToRgb(this.colors.text));
      doc.setLineWidth(0.8);
      doc.line(col3X + 4.5, y + 3, col3X + 4, y + 1);
      doc.line(col3X + 7.5, y + 3, col3X + 7.5, y + 1);
      doc.line(col3X + 4, y + 1, col3X + 7.5, y + 1);
    }

    doc.setFontSize(13);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...this.hexToRgb(this.colors.text));
    const scName = data.shoppingCenter || 'Unnamed Shopping Center';
    const col3TextWidth = colWidth - iconSpacing - 8;
    const scLines = doc.splitTextToSize(scName, col3TextWidth);

    // Draw shopping center name with wrapping
    let col3TextY = y + 5;
    const maxScLines = Math.min(scLines.length, 2); // Allow up to 2 lines for shopping center name
    for (let i = 0; i < maxScLines; i++) {
      doc.text(scLines[i], col3X + 15, col3TextY);
      col3TextY += 4.5;
    }

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...this.hexToRgb(this.colors.textLight));
    const ownerName = data.shoppingCenterOwner || 'Unknown Owner';
    const centerType = data.shoppingCenterTypeDisplayName || data.shoppingCenterType || 'Unknown Shopping Center Type';
    const ownerLines = doc.splitTextToSize(ownerName, col3TextWidth);
    const centerTypeLines = doc.splitTextToSize(centerType, col3TextWidth);

    // Draw owner name (single line, truncated if too long)
    const ownerTextY = maxScLines > 1 ? col3TextY : y + 10;
    doc.text(ownerLines[0] || ownerName, col3X + 15, ownerTextY);

    // Draw center type (wrap if needed)
    const centerTypeY = ownerTextY + 5;
    doc.text(centerTypeLines[0] || centerType, col3X + 15, centerTypeY);

    // Calculate return Y based on actual content height
    const col3Height = centerTypeY + 3 - y;
    return y + Math.max(18, col3Height);
  }

  private drawSectionHeader(doc: jsPDF, title: string, y: number): number {
    const x = this.margins.left;
    const contentWidth = this.getContentWidth();

    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...this.hexToRgb(this.colors.primary));
    doc.text(title, x, y + 5);

    this.drawLine(doc, x, y + 7, x + contentWidth, y + 7, this.colors.border, 0.3);
    return y + 11;
  }

  private drawCriticalInsights(doc: jsPDF, metrics: MetricItem[], highlights: AlertItem[], y: number): number {
    const x = this.margins.left;
    const contentWidth = this.getContentWidth();
    let currentY = y;

    currentY = this.drawSectionHeader(doc, 'CRITICAL INSIGHTS', currentY);

    const metricsWidth = this.px(245);
    const alertsStartX = x + metricsWidth + 5; // Moved further left for more text space

    // Calculate section height based on content
    const metricsHeight = metrics.length * 16;
    const highlightsToShow = Math.min(highlights.length, 10);
    const alertsHeight = highlightsToShow * 14; // Using alertRowHeight of 14
    const sectionHeight = Math.max(metricsHeight, alertsHeight);

    // Vertical divider
    this.drawLine(doc, x + metricsWidth, currentY, x + metricsWidth, currentY + sectionHeight, '#00000060', 0.3);

    // Metrics column - properly sized icons (not stretched)
    let metricsY = currentY;
    const metricIconSize = 5;
    metrics.forEach((metric, idx) => {
      // Draw icon based on type - use actual images from assets
      if (idx < 3 && this.imageAssets.metricIcons?.[idx]) {
        try {
          const img = this.imageAssets.metricIcons[idx];
          const imgProps = doc.getImageProperties(img);
          const targetHeight = 8;
          const targetWidth = Math.min(12, (imgProps.width * targetHeight) / imgProps.height);
          doc.addImage(img, 'PNG', x, metricsY, targetWidth, targetHeight);
        } catch (error) {
          // Fallback: draw simple icon shape
          doc.setDrawColor(...this.hexToRgb(this.colors.textLight));
          doc.setLineWidth(0.5);
          doc.rect(x, metricsY + 1, metricIconSize, metricIconSize, 'S');
          doc.setFillColor(...this.hexToRgb(this.colors.textLight));
          doc.rect(x + 1.5, metricsY + 2.5, 2, 2, 'F');
        }
      } else {
        // Status dot for health indicator (store status)
        const statusColor = metric.color || this.colors.success;
        doc.setFillColor(...this.hexToRgb(statusColor));
        doc.circle(x + metricIconSize/2, metricsY + metricIconSize/1.5 + 1, metricIconSize/1.5, 'F');
      }

      doc.setFontSize(14);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...this.hexToRgb(this.colors.text));
      // Convert last metric value from capital case to camel case
      const displayValue = idx === metrics.length - 1 ? this.convertToCamelCase(metric.value) : metric.value;
      doc.text(displayValue, x + 11, metricsY + 4);

      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...this.hexToRgb(this.colors.textLight));
      const labelLines = doc.splitTextToSize(metric.label, metricsWidth - 20);
      doc.text(labelLines[0] || metric.label, x + 11, metricsY + 9);

      metricsY += 16;
    });

    // Highlights/Alerts column - properly aligned icons and text
    let alertsY = currentY;
    const alertIconSize = 10; // Original icon box size
    const alertRowHeight = 14; // Original row height
    const textStartX = alertsStartX + alertIconSize + 3; // Tight gap for more text space
    const alertWidth = contentWidth - metricsWidth - 8; // Maximized width for text

    // Helper function to abbreviate long text for PDF fit
    const abbreviateText = (text: string): string => {
      return text
        .replace(/Drivetime/gi, 'DT')
        .replace(/Drive Time/gi, 'DT')
        .replace(/-Minute/gi, '-Min')
        .replace(/ Minute/gi, ' Min')
        .replace(/Estimated/gi, 'Est.')
    };

    for (let i = 0; i < highlightsToShow; i++) {
      const h = highlights[i];

      // Calculate vertical center for this row
      const rowCenterY = alertsY + alertRowHeight / 2;

      // Icon with colored background (green/red rounded square) - vertically centered
      const iconColor = h.isPositive ? this.colors.success : this.colors.danger;
      const iconY = rowCenterY - alertIconSize / 2;
      this.drawRoundedRect(doc, alertsStartX, iconY, alertIconSize, alertIconSize, 2, iconColor);

      // +/- symbol centered in icon - larger font for visibility
      doc.setFontSize(12); // Increased from original for better visibility in same box
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0, 0, 0);
      const symbolY = iconY + alertIconSize / 2 + 1.2;
      doc.text(h.isPositive ? '+' : '-', alertsStartX + alertIconSize / 2, symbolY, { align: 'center' });

      // Text block (title + description) - center-aligned with icon
      const textBlockHeight = 7;
      const textBlockStartY = rowCenterY - textBlockHeight / 2;

      // Title - at top of centered text block (with abbreviations)
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...this.hexToRgb(this.colors.text));
      const abbreviatedTitle = abbreviateText(h.title);
      const titleLines = doc.splitTextToSize(abbreviatedTitle, alertWidth);
      const titleY = textBlockStartY + 3;
      doc.text(titleLines[0] || abbreviatedTitle, textStartX, titleY);

      // Description - below title (with abbreviations)
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...this.hexToRgb(this.colors.textLight));
      const abbreviatedDesc = abbreviateText(h.description);
      const descLines = doc.splitTextToSize(abbreviatedDesc, alertWidth);
      const descY = titleY + 4;
      doc.text(descLines[0] || '', textStartX, descY);

      alertsY += alertRowHeight;
    }

    return Math.max(metricsY, alertsY) + 5;
  }

  private drawSalesPerformance(doc: jsPDF, data: SalesPerformanceData, y: number): number {
    const x = this.margins.left;
    let currentY = y;

    currentY = this.drawSectionHeader(doc, 'SALES PERFORMANCE INSIGHTS', currentY);

    if (!data?.byVolume?.length && !data?.bySqft?.length) {
      doc.setFontSize(11);
      doc.setTextColor(...this.hexToRgb(this.colors.textMuted));
      doc.text('Performance cannot be analyzed due to this store missing required data', x, currentY + 4);
      return currentY + 12;
    }

    // Determine which section to show based on selectedView
    const selectedView = data.selectedView || 'volume'; // Default to volume if not specified

    if (selectedView === 'volume') {
      // By Volume section only
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...this.hexToRgb(this.colors.text));
      doc.text('COMPARING BY ESTIMATED SALES VOLUME', x, currentY + 4);
      currentY += 8;

      if (data?.byVolume?.length) {
        currentY = this.drawPerfRow(doc, data.byVolume, currentY);
      } else {
        doc.setFontSize(10);
        doc.setTextColor(...this.hexToRgb(this.colors.textMuted));
        doc.text('Performance cannot be analyzed due to this store missing required data', x, currentY + 4);
        currentY += 10;
      }
    } else {
      // By SQFT section only
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...this.hexToRgb(this.colors.text));
      doc.text('COMPARING BY ESTIMATED SALES/SQFT', x, currentY + 4);
      currentY += 8;

      if (data?.bySqft?.length) {
        currentY = this.drawPerfRow(doc, data.bySqft, currentY);
      } else {
        doc.setFontSize(10);
        doc.setTextColor(...this.hexToRgb(this.colors.textMuted));
        doc.text('Performance cannot be analyzed due to this store missing required data', x, currentY + 4);
        currentY += 10;
      }
    }

    return currentY + 8;
  }

  private drawPerfRow(doc: jsPDF, items: PerformanceItem[], y: number): number {
    const x = this.margins.left;
    const contentWidth = this.getContentWidth();
    const itemWidth = contentWidth / 3;
    const ringSize = 7;

    // Debug log to see what colors are being received
    console.log('drawPerfRow items:', items.map(i => ({ percentile: i.percentile, color: i.color, status: i.status })));

    items.forEach((item, idx) => {
      const itemX = x + idx * itemWidth;
      const cx = itemX + ringSize;
      const cy = y + ringSize;

      // Debug log for each item
      const itemColor = item.color || '#999999';
      console.log(`Drawing ring ${idx}: percentile=${item.percentile}, color="${itemColor}"`);

      // Progress ring (larger)
      this.drawProgressRing(doc, cx, cy, ringSize - 1, item.percentile, itemColor);

      // Percentile number in center
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...this.hexToRgb(this.colors.text));
      doc.text(String(item.percentile), cx, cy + 1, { align: 'center' });

      // Text content
      const textX = itemX + ringSize * 2 + 1;
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...this.hexToRgb(this.colors.textLight));
      doc.text('This Store is a', textX, y + 4);

      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...this.hexToRgb(this.colors.text));
      doc.text(item.status || '', textX, y + 10);

      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...this.hexToRgb(this.colors.textLight));
      const desc = doc.splitTextToSize(item.description || '', itemWidth - ringSize * 2 - 8);
      doc.text(desc[0] || '', textX, y + 15);
    });

    return y + 20;
  }

  /**
   * Draws the Store Rank Table with dynamic pagination
   * Similar style to map search PDF table
   */
  private drawStoreRankTable(doc: jsPDF, data: StoreRankTableData, y: number): { finalY: number; pagesUsed: number } {
    const x = this.margins.left;
    const contentWidth = this.getContentWidth();
    let currentY = y;
    let pagesUsed = 0;

    // Draw rank header line: "Your store rank is #15 of 16 stores"
    doc.setFontSize(12);
    doc.setFont('SofiaSans-Bold', 'bold');
    doc.setTextColor(...this.hexToRgb(this.colors.primary));
    doc.text(`Your store rank is #${data.currentStoreRank} of ${data.totalStores} stores`, x, currentY );
    currentY += 6;

    // Prepare table data with two-line store names
    const tableBody: any[][] = data.rows.map(row => {
      // Create store name cell with two lines - simple string, no special styling
      const storeNameContent = row.storeName + (row.storeSubtext ? '\n' + row.storeSubtext : '');

      return [
        storeNameContent,
        row.storeCode,
        row.status,
        this.formatFitCategory(row.fit), // Format FIT category
        row.salesPerSqft,
        row.totalSalesArea,
        row.totalSalesVolume
      ];
    });

    // Calculate column widths proportionally (total contentWidth)
    // Store Name: 22%, Store Code: 10%, Status: 10%, Fit: 15%, Sales/SQFT: 11%, Total Area: 13%, Total Volume: 19%
    const colWidths = [
      contentWidth * 0.22,  // Store Name
      contentWidth * 0.10,  // Store Code
      contentWidth * 0.10,  // Status
      contentWidth * 0.16,  // Fit (increased from 0.12)
      contentWidth * 0.11,  // Sales per SQFT (decreased from 0.13)
      contentWidth * 0.12,  // Total Sales Area (decreased from 0.15)
      contentWidth * 0.19   // Total Sales Volume (increased from 0.18)
    ];

    // Track starting page
    const startPage = doc.getNumberOfPages();

    // Draw table using autoTable - proper striped theme with alternating colors
    autoTable(doc, {
      startY: currentY,
      head: [['Store Name', 'Store Code', 'Status', 'Fit', 'Sales per SQFT', 'Total Sales Area', 'Total Sales Volume']],
      body: tableBody,
      theme: 'striped',
      styles: {
        fontSize: 10,
        cellPadding: { top: 2, right: 2, bottom: 2, left: 2 },
        overflow: 'linebreak',
        valign: 'middle',
        fillColor: [255, 255, 255] // Default white background for odd rows
      },
      headStyles: {
        fillColor: [233, 233, 233], // Light gray header
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        halign: 'left',
        fontSize: 10
      },
      // Alternating row colors: white (255,255,255) and light gray (248,248,248)
      alternateRowStyles: {
        fillColor: [248, 248, 248] // Light gray for even rows
      },
      columnStyles: {
        0: { cellWidth: colWidths[0], halign: 'left' },   // Store Name - left aligned for readability
        1: { cellWidth: colWidths[1], halign: 'left' }, // Store Code
        2: { cellWidth: colWidths[2], halign: 'left' }, // Status
        3: { cellWidth: colWidths[3], halign: 'left' }, // Fit
        4: { cellWidth: colWidths[4], halign: 'left' },  // Sales per SQFT
        5: { cellWidth: colWidths[5], halign: 'left' },  // Total Sales Area
        6: { cellWidth: colWidths[6], halign: 'left', textColor: [46, 144, 250] }   // Total Sales Volume - blue color
      },
      margin: { left: x, right: this.margins.right },
      // Handle page breaks automatically
      showHead: 'everyPage',
      // Track page changes and draw pattern line on first page
      didDrawPage: (pageData) => {
        pagesUsed++;
        // If this is the first page (page 1) and we're drawing the table, add pattern line
        if (pageData.pageNumber === 1) {
          this.drawPatternLine(doc);
        }
      }
    });

    // Get final Y position after table
    const finalY = (doc as any).lastAutoTable?.finalY || currentY + 20;
    pagesUsed = doc.getNumberOfPages() - startPage + 1;

    return { finalY, pagesUsed };
  }

  // ============================================================================
  // STORE DETAIL PDF - SECOND PAGE (DEMOGRAPHICS)
  // ============================================================================

  private drawDemographicHeader(doc: jsPDF, data: DemographicData, y: number): number {
    const x = this.margins.left;
    const contentWidth = this.getContentWidth();
    let currentY = y;

    // Header row with title and region
    doc.setFontSize(16);
    doc.setFont('SofiaSans-Bold', 'bold');
    doc.setTextColor(...this.hexToRgb(this.colors.primary));
    doc.text('DEMOGRAPHIC PROFILE', x, currentY + 5);

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...this.hexToRgb(this.colors.text));
    const regionLabel = 'Current Region: ';
    const regionValue = data.regionText ;
    const regionLabelWidth = doc.getTextWidth(regionLabel);
    doc.text(regionLabel, x + contentWidth - regionLabelWidth - doc.getTextWidth(regionValue) - 2, currentY + 5);
    doc.setFont('helvetica', 'bold');
    doc.text(regionValue, x + contentWidth - doc.getTextWidth(regionValue), currentY + 5);

    this.drawLine(doc, x, currentY + 8, x + contentWidth, currentY + 8, this.colors.border, 0.3);
    currentY += 12;

    // Description text
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...this.hexToRgb(this.colors.textMuted));
    const desc = "This is a profile of the population in this store's local area. Consider this profile to be the first step towards understanding this store's target customer base, and how the local demographics may be affecting this store's performance.";
    const descLines = doc.splitTextToSize(desc, contentWidth);
    doc.text(descLines, x, currentY + 4);
    currentY += descLines.length * 4 + 6;

    return currentY;
  }

  private drawEthnicityAndPopulation(doc: jsPDF, data: DemographicData, y: number): number {
   let currentY = y;

    // Center the content boxes more precisely in the middle
    const ethWidth = this.px(161);
    const popWidth = this.px(245);
    const totalBoxWidth = ethWidth + 12 + popWidth;
    const centerOffset = (this.PAGE_WIDTH - totalBoxWidth) / 2;
    const centeredX = centerOffset;

    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...this.hexToRgb(this.colors.text));
    doc.text('ETHNICITY', centeredX + ethWidth / 2, currentY + 4, { align: 'center' });
    doc.text('POPULATION', centeredX + ethWidth + 12 + popWidth / 2, currentY + 4, { align: 'center' });

    this.drawLine(doc, centeredX, currentY + 7, centeredX + ethWidth, currentY + 7, this.colors.text, 0.3);
    this.drawLine(doc, centeredX + ethWidth + 12, currentY + 7, centeredX + ethWidth + 12 + popWidth, currentY + 7, this.colors.text, 0.3);
    currentY += 11;

    // Ethnicity box
    const ethHeight = this.px(95);
    this.drawRect(doc, centeredX, currentY, ethWidth, ethHeight, this.colors.ethnicityPurple);

    const ethnicities = [
      { label: 'Other', value: data.ethnicity.other },
      { label: 'Asian', value: data.ethnicity.asian },
      { label: 'White', value: data.ethnicity.white },
      { label: 'Hispanic', value: data.ethnicity.hispanic },
      { label: 'Black', value: data.ethnicity.black }
    ].sort((a, b) => b.value - a.value);

    let ethY = currentY + 6;
    doc.setFontSize(10);
    ethnicities.forEach(eth => {
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'normal');
      doc.text(eth.label, centeredX + 4, ethY);
      doc.setFont('helvetica', 'bold');
      doc.text(`${eth.value}%`, centeredX + ethWidth - 4, ethY, { align: 'right' });
      ethY += 6;
    });

    // Population cards
    const cardW = (popWidth - 8) / 3;
    const cardH = ethHeight;
    const popX = centeredX + ethWidth + 12;

    // Card 1: Historical 2yr Growth
    this.drawRect(doc, popX, currentY, cardW, cardH, this.colors.populationBlue);
    if (this.imageAssets.vectorIcons?.growth) {
      try {
        const iconSize = 12;
        const imgProps = doc.getImageProperties(this.imageAssets.vectorIcons.growth);
        const aspectRatio = imgProps.width / imgProps.height;
        const iconW = aspectRatio >= 1 ? iconSize : iconSize * aspectRatio;
        const iconH = aspectRatio >= 1 ? iconSize / aspectRatio : iconSize;
        doc.addImage(this.imageAssets.vectorIcons.growth, 'PNG', popX + cardW/2 - iconW/2, currentY + 4, iconW, iconH);
      } catch {}
    }
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text('Historical', popX + cardW / 2, currentY + cardH - 12, { align: 'center' });
    doc.text('2yr Growth', popX + cardW / 2, currentY + cardH - 7, { align: 'center' });
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...this.hexToRgb(this.colors.text));
    doc.text(`${data.population.growth2yr > 0 ? '+' : ''}${data.population.growth2yr}%`, popX + cardW / 2, currentY + cardH + 5, { align: 'center' });

    // Card 2: Total Population
    const c2x = popX + cardW + 4;
    this.drawRect(doc, c2x, currentY, cardW, cardH, this.colors.populationBlue);
    if (this.imageAssets.vectorIcons?.population) {
      try {
        const iconSize = 12;
        const imgProps = doc.getImageProperties(this.imageAssets.vectorIcons.population);
        const aspectRatio = imgProps.width / imgProps.height;
        const iconW = aspectRatio >= 1 ? iconSize : iconSize * aspectRatio;
        const iconH = aspectRatio >= 1 ? iconSize / aspectRatio : iconSize;
        doc.addImage(this.imageAssets.vectorIcons.population, 'PNG', c2x + cardW/2 - iconW/2, currentY + 4, iconW, iconH);
      } catch {}
    }
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text('Total', c2x + cardW / 2, currentY + cardH - 12, { align: 'center' });
    doc.text('Population', c2x + cardW / 2, currentY + cardH - 7, { align: 'center' });
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...this.hexToRgb(this.colors.text));
    doc.text(data.population.total.toLocaleString('en-US'), c2x + cardW / 2, currentY + cardH + 5, { align: 'center' });

    // Card 3: Projected 5yr Growth
    const c3x = c2x + cardW + 4;
    this.drawRect(doc, c3x, currentY, cardW, cardH, this.colors.populationBlue);
    if (this.imageAssets.vectorIcons?.projected) {
      try {
        const iconSize = 12;
        const imgProps = doc.getImageProperties(this.imageAssets.vectorIcons.projected);
        const aspectRatio = imgProps.width / imgProps.height;
        const iconW = aspectRatio >= 1 ? iconSize : iconSize * aspectRatio;
        const iconH = aspectRatio >= 1 ? iconSize / aspectRatio : iconSize;
        doc.addImage(this.imageAssets.vectorIcons.projected, 'PNG', c3x + cardW/2 - iconW/2, currentY + 4, iconW, iconH);
      } catch {}
    }
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text('Projected', c3x + cardW / 2, currentY + cardH - 12, { align: 'center' });
    doc.text('5yr Growth', c3x + cardW / 2, currentY + cardH - 7, { align: 'center' });
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...this.hexToRgb(this.colors.text));
    const p5 = data.population.projected5yr || data.population.householdGrowth2yr || 0;
    doc.text(`${p5 > 0 ? '+' : ''}${p5}%`, c3x + cardW / 2, currentY + cardH + 5, { align: 'center' });

    return currentY + cardH + 14;
  }

  private drawPeopleAndIncome(doc: jsPDF, data: DemographicData, y: number): number {
    const x = this.margins.left;
    const contentWidth = this.getContentWidth();
    let currentY = y;

    // People & Income section headers - centered more precisely in the middle
    const pWidth = this.px(243);
    const iWidth = this.px(162);
    const totalPeopleIncomeWidth = pWidth + 12 + iWidth;
    const peopleCenterOffset = (this.PAGE_WIDTH - totalPeopleIncomeWidth) / 2;
    const peopleCenteredX = peopleCenterOffset;

    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...this.hexToRgb(this.colors.text));
    doc.text('PEOPLE', peopleCenteredX + pWidth / 2, currentY + 4, { align: 'center' });
    doc.text('INCOME', peopleCenteredX + pWidth + 12 + iWidth / 2, currentY + 4, { align: 'center' });

    this.drawLine(doc, peopleCenteredX, currentY + 7, peopleCenteredX + pWidth, currentY + 7, this.colors.text, 0.3);
    this.drawLine(doc, peopleCenteredX + pWidth + 12, currentY + 7, peopleCenteredX + pWidth + 12 + iWidth, currentY + 7, this.colors.text, 0.3);
    currentY += 11;

    // People cards
    const pCardW = (pWidth - 8) / 3;
    const pCardH = this.px(95);

    // Average Age
    this.drawRect(doc, peopleCenteredX, currentY, pCardW, pCardH, this.colors.peopleBlue);
    if (this.imageAssets.vectorIcons?.age) {
      try {
        const iconSize = 12;
        const imgProps = doc.getImageProperties(this.imageAssets.vectorIcons.age);
        const aspectRatio = imgProps.width / imgProps.height;
        const iconW = aspectRatio >= 1 ? iconSize : iconSize * aspectRatio;
        const iconH = aspectRatio >= 1 ? iconSize / aspectRatio : iconSize;
        doc.addImage(this.imageAssets.vectorIcons.age, 'PNG', peopleCenteredX + pCardW/2 - iconW/2, currentY + 4, iconW, iconH);
      } catch {}
    }
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text('Average', peopleCenteredX + pCardW / 2, currentY + pCardH - 12, { align: 'center' });
    doc.text('Age', peopleCenteredX + pCardW / 2, currentY + pCardH - 7, { align: 'center' });
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...this.hexToRgb(this.colors.text));
    doc.text(String(round(data.people.averageAge,1)), peopleCenteredX + pCardW / 2, currentY + pCardH + 5, { align: 'center' });

    // College Educated
    const p2x = peopleCenteredX + pCardW + 4;
    this.drawRect(doc, p2x, currentY, pCardW, pCardH, this.colors.peopleBlue);
    if (this.imageAssets.vectorIcons?.educated) {
      try {
        const iconSize = 10;
        doc.addImage(this.imageAssets.vectorIcons.educated, 'PNG', p2x + pCardW/2 - iconSize/2, currentY + 6, iconSize, iconSize);
      } catch {}
    }
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text('College', p2x + pCardW / 2, currentY + pCardH - 12, { align: 'center' });
    doc.text('Educated', p2x + pCardW / 2, currentY + pCardH - 7, { align: 'center' });
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...this.hexToRgb(this.colors.text));
    doc.text(`${round(data.people.collegeEducated,2)}%`, p2x + pCardW / 2, currentY + pCardH + 5, { align: 'center' });

    // Household Size
    const p3x = p2x + pCardW + 4;
    this.drawRect(doc, p3x, currentY, pCardW, pCardH, this.colors.peopleBlue);
    if (this.imageAssets.vectorIcons?.household) {
      try {
        const iconSize = 10;
        doc.addImage(this.imageAssets.vectorIcons.household, 'PNG', p3x + pCardW/2 - iconSize/2, currentY + 6, iconSize, iconSize);
      } catch {}
    }
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text('Household', p3x + pCardW / 2, currentY + pCardH - 12, { align: 'center' });
    doc.text('Size', p3x + pCardW / 2, currentY + pCardH - 7, { align: 'center' });
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...this.hexToRgb(this.colors.text));
    doc.text(String(round(data.people.householdSize,1)), p3x + pCardW / 2, currentY + pCardH + 5, { align: 'center' });

    // Income cards
    const iCardW = (iWidth - 4) / 2;
    const iX = peopleCenteredX + pWidth + 12;

    // Average Income
    this.drawRect(doc, iX, currentY, iCardW, pCardH, this.colors.incomePurple);
    // Primary dollar icon (larger, centered, semi-transparent)
    if (this.imageAssets.dollarIcon) {
      try {
        doc.addImage(this.imageAssets.dollarIcon, 'PNG', iX + iCardW/2 - 6, currentY + 4, 12, 12);
      } catch {}
    }
    // Secondary home icon (smaller, bottom right)
    if (this.imageAssets.homeIcon) {
      try {
        doc.addImage(this.imageAssets.homeIcon, 'PNG', iX + iCardW/2 , currentY + 10, 7, 7);
      } catch {}
    }
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text('Average', iX + iCardW / 2, currentY + pCardH - 12, { align: 'center' });
    doc.text('Income', iX + iCardW / 2, currentY + pCardH - 7, { align: 'center' });
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...this.hexToRgb(this.colors.text));
    doc.text(`$${data.income.average.toLocaleString('en-US')}`, iX + iCardW / 2, currentY + pCardH + 5, { align: 'center' });

    // Median Income
    const i2x = iX + iCardW + 4;
    this.drawRect(doc, i2x, currentY, iCardW, pCardH, this.colors.incomePurple);
    // Primary dollar icon (larger, centered, semi-transparent)
    if (this.imageAssets.dollarIcon) {
      try {
        doc.addImage(this.imageAssets.dollarIcon, 'PNG', i2x + iCardW/2 - 6, currentY + 4, 12, 12);

      } catch {}
    }
    // Secondary home icon (smaller, bottom right)
    if (this.imageAssets.homeIcon) {
      try {
        doc.addImage(this.imageAssets.homeIcon, 'PNG', i2x + iCardW/2 , currentY + 10, 7, 7);

      } catch {}
    }
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text('Median', i2x + iCardW / 2, currentY + pCardH - 12, { align: 'center' });
    doc.text('Income', i2x + iCardW / 2, currentY + pCardH - 7, { align: 'center' });
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...this.hexToRgb(this.colors.text));
    doc.text(`$${data.income.median.toLocaleString('en-US')}`, i2x + iCardW / 2, currentY + pCardH + 5, { align: 'center' });

    currentY += pCardH + 12;

    // Data provider text - right aligned
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...this.hexToRgb('#707070'));
    doc.text('Demographic data provided by Synerpos Technologies, Inc.', x + contentWidth, currentY, { align: 'right' });

    return currentY + 8;
  }

  private drawBannerAnalysis(doc: jsPDF, data: BannerAnalysisData, y: number): number {
    const x = this.margins.left;
    const contentWidth = this.getContentWidth();
    let currentY = y;

    // Title - larger font
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...this.hexToRgb(this.colors.primary));
    doc.text(`BANNER ANALYSIS: ${data.bannerName || 'Unknown'}`, x, currentY );

    // Market name
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...this.hexToRgb(this.colors.textMuted));
    doc.text(`In the ${data.marketName || 'Market Area'}`, x, currentY + 6);

    // Description
    doc.setFontSize(9);
    doc.text("Compare the entire banner's performance in the market. How does this store's banner compare to all other banners with a presence in the market?", x, currentY + 12);

    this.drawLine(doc, x, currentY + 19, x + contentWidth, currentY + 19, this.colors.border, 0.3);
    currentY += 24;

    // Performance items (3 columns) - with larger fonts and better spacing
    const items = [data.salesSqft, data.storeCount, data.salesVolume].filter(item => item && item.status);
    const itemWidth = contentWidth / 3;
    const ringSize = 6;

    // Debug log for banner analysis items
    console.log('drawBannerAnalysis items:', items.map(i => ({ percentile: i?.percentile, color: i?.color, status: i?.status })));

    items.forEach((item, idx) => {
      if (!item) return;
      const itemX = x + idx * itemWidth;
      const cx = itemX + ringSize;
      const cy = currentY + ringSize;

      // Debug log for each banner item
      const itemColor = item.color || '#999999';
      console.log(`Drawing banner ring ${idx}: percentile=${item.percentile}, color="${itemColor}"`);

      // Progress ring - larger
      this.drawProgressRing(doc, cx, cy, ringSize - 1, item.percentile || 0, itemColor);

      // Percentile in center
      doc.setFontSize(7);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...this.hexToRgb(this.colors.text));
      doc.text(String(item.percentile), cx, cy + 1, { align: 'center' });

      // Text content
      const textX = itemX + ringSize * 2 + 4;

      // Prefix text - "This Banner is a"
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...this.hexToRgb(this.colors.textLight));
      doc.text(item.prefix || 'This Banner is a', textX, currentY + 4);

      // Status (e.g., "Top Performer")
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...this.hexToRgb(this.colors.text));
      doc.text(item.status || 'N/A', textX, currentY + 10);

      // Description
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...this.hexToRgb(this.colors.textLight));
      const desc = doc.splitTextToSize(item.description || '', itemWidth - ringSize * 2 - 8);
      doc.text(desc[0] || '', textX, currentY + 15);
    });

    return currentY + 22;
  }

  /**
   * Draw Services & Amenities section (Part 1 of Additional Insights)
   * Estimated height: ~50-60mm depending on data
   */
  private drawServicesAndAmenities(doc: jsPDF, data: AdditionalInsightsData, y: number, drawHeader: boolean = true): number {
    const x = this.margins.left;
    let currentY = y + 5;

    if (drawHeader) {
      currentY = this.drawSectionHeader(doc, 'ADDITIONAL INSIGHTS', currentY);
    }

    // Services & Amenities section with icon
    const iconSize = 10;

    // Draw Services & Amenities icon
    if ((this.imageAssets as any).servicesIcon) {
      try {
        doc.addImage((this.imageAssets as any).servicesIcon, 'PNG', x, currentY - 1, iconSize, iconSize);
      } catch {
        doc.setFillColor(...this.hexToRgb(this.colors.primary));
        doc.rect(x, currentY + 1, iconSize * 0.6, iconSize * 0.6, 'F');
      }
    } else {
      doc.setFillColor(...this.hexToRgb(this.colors.primary));
      doc.rect(x, currentY + 1, iconSize * 0.6, iconSize * 0.6, 'F');
    }

    // Services count header
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...this.hexToRgb(this.colors.text));
    doc.text(`${data.servicesCount} Services & Amenities`, x + iconSize + 2, currentY + 4);

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...this.hexToRgb(this.colors.textLight));
    doc.text('Offered at this location', x + iconSize + 2, currentY + 10);
    currentY += 14;

    const tablesY = currentY;

    // Services table (left side) - dynamically determine columns based on data
    const servicesCount = data.services.length;
    const servicesCols = servicesCount === 0 ? 1 : Math.min(2, servicesCount); // 1 or 2 columns max
    const servicesTableWidth = servicesCols === 1 ? 30 : 55;
    const serviceColWidth = servicesTableWidth / servicesCols;

    const servicesBody: string[][] = [];
    if (servicesCols === 1) {
      // Single column layout
      data.services.forEach(service => servicesBody.push([service]));
    } else {
      // Two column layout
      for (let i = 0; i < data.services.length; i += 2) {
        const row: string[] = [data.services[i]];
        if (data.services[i + 1]) {
          row.push(data.services[i + 1]);
        } else if (i + 1 < data.services.length || data.services.length % 2 !== 0) {
          // Only add empty cell if we need to complete the row for existing items
        }
        servicesBody.push(row);
      }
    }

    if (servicesBody.length === 0) {
      servicesBody.push(['N/A']);
    }

    // Build dynamic column styles for services
    const servicesColStyles: { [key: string]: Partial<{ cellWidth: number }> } = {};
    for (let i = 0; i < servicesCols; i++) {
      servicesColStyles[String(i)] = { cellWidth: serviceColWidth };
    }

    // Build dynamic header for services
    const servicesHead: string[] = ['Services'];
    for (let i = 1; i < servicesCols; i++) {
      servicesHead.push('');
    }

    autoTable(doc, {
      startY: tablesY,
      head: [servicesHead],
      body: servicesBody,
      theme: 'grid',
      styles: { fontSize: 9, cellPadding: 2, fontStyle: 'bold' },
      headStyles: { fillColor: [251, 251, 251], textColor: [0, 0, 0], fontStyle: 'bold', fontSize: 10, lineWidth: 0.1 },
      columnStyles: servicesColStyles,
      margin: { left: x },
      tableWidth: servicesTableWidth
    });

    // Amenities table (right side) - dynamically determine columns based on data
    const amenitiesCount = data.amenities.length;
    // Determine optimal number of columns: 1, 2, or 3 based on item count
    let amenitiesCols = 1;
    if (amenitiesCount >= 6) {
      amenitiesCols = 3;
    } else if (amenitiesCount >= 2) {
      amenitiesCols = Math.min(3, amenitiesCount);
    }

    const amenitiesTableWidth = amenitiesCols === 1 ? 50 : amenitiesCols === 2 ? 90 : 135;
    const amenityColWidth = amenitiesTableWidth / amenitiesCols;
    const amenitiesStartX = x + servicesTableWidth + 10;

    const amenitiesBody: string[][] = [];
    if (amenitiesCols === 1) {
      // Single column layout
      data.amenities.forEach(amenity => amenitiesBody.push([amenity]));
    } else if (amenitiesCols === 2) {
      // Two column layout
      for (let i = 0; i < data.amenities.length; i += 2) {
        const row: string[] = [data.amenities[i]];
        if (data.amenities[i + 1]) {
          row.push(data.amenities[i + 1]);
        }
        amenitiesBody.push(row);
      }
    } else {
      // Three column layout
      for (let i = 0; i < data.amenities.length; i += 3) {
        const row: string[] = [data.amenities[i]];
        if (data.amenities[i + 1]) {
          row.push(data.amenities[i + 1]);
        }
        if (data.amenities[i + 2]) {
          row.push(data.amenities[i + 2]);
        }
        amenitiesBody.push(row);
      }
    }

    if (amenitiesBody.length === 0) {
      amenitiesBody.push(['N/A']);
    }

    // Build dynamic column styles for amenities
    const amenitiesColStyles: { [key: string]: Partial<{ cellWidth: number }> } = {};
    for (let i = 0; i < amenitiesCols; i++) {
      amenitiesColStyles[String(i)] = { cellWidth: amenityColWidth };
    }

    // Build dynamic header for amenities
    const amenitiesHead: string[] = ['Amenities'];
    for (let i = 1; i < amenitiesCols; i++) {
      amenitiesHead.push('');
    }

    autoTable(doc, {
      startY: tablesY,
      head: [amenitiesHead],
      body: amenitiesBody,
      theme: 'grid',
      styles: { fontSize: 9, cellPadding: 2, fontStyle: 'bold' },
      headStyles: { fillColor: [251, 251, 251], textColor: [0, 0, 0], fontStyle: 'bold', fontSize: 10, lineWidth: 0.1 },
      columnStyles: amenitiesColStyles,
      margin: { left: amenitiesStartX },
      tableWidth: amenitiesTableWidth
    });

    return (doc as any).lastAutoTable?.finalY || currentY + 25;
  }

  /**
   * Draw Analyst Ratings section (Part 2 of Additional Insights)
   * Estimated height: ~50-70mm depending on data
   */
  private drawAnalystRatings(doc: jsPDF, data: AdditionalInsightsData, y: number): number {
    const x = this.margins.left;
    let currentY = y;
    const iconSize = 10;

    // Analyst Ratings section with icon
    if ((this.imageAssets as any).ratingsIcon) {
      try {
        doc.addImage((this.imageAssets as any).ratingsIcon, 'PNG', x, currentY - 1, iconSize, iconSize, undefined, 'FAST');
      } catch {
        doc.setFillColor(...this.hexToRgb(this.colors.primary));
        doc.rect(x, currentY + 1, iconSize * 0.6, iconSize * 0.6, 'F');
      }
    } else {
      doc.setFillColor(...this.hexToRgb(this.colors.primary));
      doc.rect(x, currentY + 1, iconSize * 0.6, iconSize * 0.6, 'F');
    }

    // Analyst Ratings header
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...this.hexToRgb(this.colors.text));
    doc.text('Analyst Ratings Available', x + iconSize + 2, currentY + 4);

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...this.hexToRgb(this.colors.textLight));
    doc.text(data.analystRatingDate ? `As of ${data.analystRatingDate}` : 'Not yet evaluated', x + iconSize + 2, currentY + 10);
    currentY += 14;

    const ratingsY = currentY;

    // Helper function to check if second pair exists in any row
    const hasSecondPair = (ratings: Array<Array<{name?: string; rating?: string}>>): boolean => {
      return ratings.some(r => r[1]?.name || r[1]?.rating);
    };

    // Helper function to build rating rows dynamically
    const buildRatingRows = (ratings: Array<Array<{name?: string; rating?: string}>>, useSecondPair: boolean): string[][] => {
      return ratings.map(r => {
        const row: string[] = [r[0]?.name || '', r[0]?.rating || ''];
        if (useSecondPair) {
          row.push(r[1]?.name || '', r[1]?.rating || '');
        }
        return row;
      });
    };

    // Store Ratings table
    let storeRatingsTableWidth = 90;
    if (data.storeRatings.length > 0) {
      const useSecondPair = hasSecondPair(data.storeRatings);
      const body = buildRatingRows(data.storeRatings, useSecondPair);
      const colCount = useSecondPair ? 4 : 2;
      storeRatingsTableWidth = useSecondPair ? 90 : 50;

      // Build dynamic header
      const head: string[] = ['Store Ratings'];
      for (let i = 1; i < colCount; i++) head.push('');

      // Build dynamic column styles
      const colStyles: { [key: string]: Partial<{ halign: 'left' | 'center' | 'right'; cellWidth: number }> } = {};
      if (useSecondPair) {
        colStyles['0'] = { cellWidth: 35 };
        colStyles['1'] = { halign: 'center', cellWidth: 10 };
        colStyles['2'] = { cellWidth: 35 };
        colStyles['3'] = { halign: 'center', cellWidth: 10 };
      } else {
        colStyles['0'] = { cellWidth: 40 };
        colStyles['1'] = { halign: 'center', cellWidth: 10 };
      }

      autoTable(doc, {
        startY: ratingsY,
        head: [head],
        body,
        theme: 'grid',
        styles: { fontSize: 9, cellPadding: 2, fontStyle: 'bold' },
        headStyles: { fillColor: [251, 251, 251], textColor: [0, 0, 0], fontStyle: 'bold', fontSize: 10, lineWidth: 0.1 },
        columnStyles: colStyles,
        margin: { left: x },
        tableWidth: storeRatingsTableWidth
      });
    }

    // Parking/Cotenant Ratings table
    if (data.parkingLotRatings.length > 0 || data.cotenantRatings.length > 0) {
      // Check if any parking or cotenant rating has second pair
      const parkingHasSecondPair = hasSecondPair(data.parkingLotRatings);
      const cotenantHasSecondPair = hasSecondPair(data.cotenantRatings);
      const useSecondPair = parkingHasSecondPair || cotenantHasSecondPair;

      const body: string[][] = buildRatingRows(data.parkingLotRatings, useSecondPair);
      if (data.cotenantRatings.length > 0) {
        // Add cotenant header row
        const cotenantHeaderRow: string[] = ['Cotenant Ratings', ''];
        if (useSecondPair) cotenantHeaderRow.push('', '');
        body.push(cotenantHeaderRow);

        // Add cotenant rating rows
        const cotenantRows = buildRatingRows(data.cotenantRatings, useSecondPair);
        cotenantRows.forEach(row => body.push(row));
      }

      const colCount = useSecondPair ? 4 : 2;
      const parkingTableWidth = useSecondPair ? 90 : 50;

      // Build dynamic header
      const head: string[] = ['Parking Lot Ratings'];
      for (let i = 1; i < colCount; i++) head.push('');

      // Build dynamic column styles
      const colStyles: { [key: string]: Partial<{ halign: 'left' | 'center' | 'right'; cellWidth: number }> } = {};
      if (useSecondPair) {
        colStyles['0'] = { cellWidth: 35 };
        colStyles['1'] = { halign: 'center', cellWidth: 10 };
        colStyles['2'] = { cellWidth: 35 };
        colStyles['3'] = { halign: 'center', cellWidth: 10 };
      } else {
        colStyles['0'] = { cellWidth: 40 };
        colStyles['1'] = { halign: 'center', cellWidth: 10 };
      }

      autoTable(doc, {
        startY: ratingsY,
        head: [head],
        body,
        theme: 'grid',
        styles: { fontSize: 9, cellPadding: 2, fontStyle: 'bold' },
        headStyles: { fillColor: [251, 251, 251], textColor: [0, 0, 0], fontStyle: 'bold', fontSize: 10, lineWidth: 0.1 },
        columnStyles: colStyles,
        didParseCell: (cellData) => {
          if (cellData.cell.text[0] === 'Cotenant Ratings') {
            cellData.cell.styles.fontStyle = 'bold';
            cellData.cell.styles.fontSize = 10;
            cellData.cell.styles.textColor = [0, 0, 0];
            cellData.cell.styles.fillColor = [251, 251, 251];
          }
        },
        margin: { left: x + storeRatingsTableWidth + 10 },
        tableWidth: parkingTableWidth
      });
    }

    return (doc as any).lastAutoTable?.finalY || currentY + 25;
  }


  // private drawFooter(doc: jsPDF): void {
  //   const y = this.PAGE_HEIGHT - this.px(35);
  //   const x = this.margins.left;

  //   // Retailstat logo
  //   if (this.imageAssets.retailstatLogo) {
  //     try {
  //       doc.addImage(this.imageAssets.retailstatLogo, 'PNG', x, y - 2, this.px(30), this.px(30));
  //     } catch { /* ignore */ }
  //   }

  //   // Footer text
  //   const textX = x + this.px(40);
  //   doc.setFontSize(9);
  //   doc.setFont('helvetica', 'normal');
  //   doc.setTextColor(...this.hexToRgb(this.colors.footerText));
  //   doc.text('Powered by ', textX, y + 5);

  //   doc.setTextColor(...this.hexToRgb(this.colors.footerBrand));
  //   doc.text('Retailstat', textX + doc.getTextWidth('Powered by '), y + 5);

  //   doc.setTextColor(...this.hexToRgb(this.colors.footerText));
  //   doc.text(' All Rights Reserved.', textX + doc.getTextWidth('Powered by Retailstat'), y + 5);
  // }

  private drawLastPageFooter(doc: jsPDF, contentEndY: number): void {
    const footerY = contentEndY + 15;
    const centerX = this.PAGE_WIDTH / 2;

    // Draw RetailStat logo centered
    if (this.footerLogo) {
      try {
        const logoWidth = 20;
        const logoHeight = 20;
        doc.addImage(
          this.footerLogo,
          'PNG',
          centerX - logoWidth / 2,
          footerY,
          logoWidth,
          logoHeight
        );
      } catch { /* ignore */ }
    }

    // Draw "Powered by Retailstat All Rights Reserved." in one line
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...this.hexToRgb(this.colors.footerText));
    const poweredByText = 'Powered by ';
    const retailstatText = 'Retailstat';
    const rightsText = ' All Rights Reserved.';

    const fullText = poweredByText + retailstatText + rightsText;
    const fullTextWidth = doc.getTextWidth(fullText);
    const textStartX = centerX - fullTextWidth / 2;

    doc.text(poweredByText, textStartX, footerY + 25);
    doc.setTextColor(...this.hexToRgb(this.colors.footerBrand));
    doc.text(retailstatText, textStartX + doc.getTextWidth(poweredByText), footerY + 25);
    doc.setTextColor(...this.hexToRgb(this.colors.footerText));
    doc.text(rightsText, textStartX + doc.getTextWidth(poweredByText + retailstatText), footerY + 25);
  }

  // ============================================================================
  // STORE DETAIL PDF GENERATOR
  // ============================================================================

  async generateStoreDetailPdf(
    headerData: StoreHeaderData,
    metrics: MetricItem[],
    highlights: AlertItem[],
    salesPerformance: SalesPerformanceData,
    demographics: DemographicData,
    bannerAnalysis: BannerAnalysisData,
    additionalInsights: AdditionalInsightsData,
    filename: string,
    storeRankTable?: StoreRankTableData // Optional store rank table data
  ): Promise<void> {
    // Preload images before generating PDF
    await this.preloadImages();

    // Resolve definition display names
    const fitDefinition = headerData.fit ? await this.definitionService.findOneFit(headerData.fit as FitType).toPromise() : null;
    const shoppingCenterDefinition = headerData.shoppingCenterType ? await this.definitionService.findOneShoppingCenterType(headerData.shoppingCenterType as ShoppingCenterType).toPromise() : null;

    const enrichedHeaderData = {
      ...headerData,
      fitDisplayName: fitDefinition?.displayName || headerData.fit,
      shoppingCenterTypeDisplayName: shoppingCenterDefinition?.displayName || headerData.shoppingCenterType
    };

    const doc = new jsPDF('p', 'mm', 'letter');
    let y = this.margins.top;

    // Page dimensions for space checking
    const pageBottom = this.PAGE_HEIGHT - 15; // Leave 15mm for pattern line
    const getAvailableSpace = () => pageBottom - y;

    // Estimated section heights (in mm)
    const SECTION_HEIGHTS = {
      SERVICES_AMENITIES: 80,  // Increased from 55 to ensure tables don't split
      ANALYST_RATINGS: 80,     // Increased from 60 to ensure tables don't split
      DEMOGRAPHIC_HEADER: 15,
      ETHNICITY_POPULATION: 70,
      PEOPLE_INCOME: 75,
      BANNER_ANALYSIS: 35
    };

    // Helper function to start a new page
    const startNewPage = () => {
      this.drawPatternLine(doc);
      doc.addPage();
      y = this.margins.top;
    };

    // ============================================================================
    // PAGE 1: Store Header, Critical Insights, Sales Performance
    // ============================================================================
    y = await this.drawStoreHeader(doc, enrichedHeaderData, y);
    y = this.drawCriticalInsights(doc, metrics, highlights, y + 2);
    y = this.drawSalesPerformance(doc, salesPerformance, y + 3);

    // Draw Store Rank Table if data is provided (after Sales Performance)
    if (storeRankTable && storeRankTable.rows && storeRankTable.rows.length > 0) {
      const tableResult = this.drawStoreRankTable(doc, storeRankTable, y + 3);
      y = tableResult.finalY;
    }

    // ============================================================================
    // SERVICES & AMENITIES - Check if fits on current page
    // ============================================================================
    if (getAvailableSpace() >= SECTION_HEIGHTS.SERVICES_AMENITIES) {
      // Fits on current page
      y = this.drawServicesAndAmenities(doc, additionalInsights, y + 5, true);
    } else {
      // Start new page for Services & Amenities (startNewPage handles pattern line)
      startNewPage();
      y = this.drawServicesAndAmenities(doc, additionalInsights, y, true);
    }

    // ============================================================================
    // ANALYST RATINGS - Check if fits on current page
    // ============================================================================
    if (getAvailableSpace() >= SECTION_HEIGHTS.ANALYST_RATINGS) {
      // Fits on current page
      y = this.drawAnalystRatings(doc, additionalInsights, y + 20);
    } else {
      // Start new page for Analyst Ratings (startNewPage handles pattern line)
      startNewPage();
      y = this.drawAnalystRatings(doc, additionalInsights, y);
    }

    // ============================================================================
    // DEMOGRAPHIC HEADER & ETHNICITY/POPULATION - Check if fits
    // ============================================================================
    const demographicPart1Height = SECTION_HEIGHTS.DEMOGRAPHIC_HEADER + SECTION_HEIGHTS.ETHNICITY_POPULATION;
    if (getAvailableSpace() >= demographicPart1Height) {
      // Fits on current page
      y = this.drawDemographicHeader(doc, demographics, y + 13);
      y = this.drawEthnicityAndPopulation(doc, demographics, y);
    } else {
      // Start new page for Demographics Part 1 (startNewPage handles pattern line)
      startNewPage();
      y = this.drawDemographicHeader(doc, demographics, y);
      y = this.drawEthnicityAndPopulation(doc, demographics, y);
    }

    // ============================================================================
    // PEOPLE & INCOME - Check if fits on current page
    // ============================================================================
    if (getAvailableSpace() >= SECTION_HEIGHTS.PEOPLE_INCOME) {
      // Fits on current page
      y = this.drawPeopleAndIncome(doc, demographics, y );
    } else {
      // Start new page for People & Income (startNewPage handles pattern line)
      startNewPage();
      y = this.drawPeopleAndIncome(doc, demographics, y);
    }

    // ============================================================================
    // BANNER ANALYSIS (if many highlights) - Check if fits
    // ============================================================================
      if (getAvailableSpace() >= SECTION_HEIGHTS.BANNER_ANALYSIS) {
        y = this.drawBannerAnalysis(doc, bannerAnalysis, y );
      } else {
        // Start new page for Banner Analysis (startNewPage handles pattern line)
        startNewPage();
        y = this.drawBannerAnalysis(doc, bannerAnalysis, y);
      }

    // ============================================================================
    // FOOTER - Always at the end
    // ============================================================================
    this.drawLastPageFooter(doc, y);

    // Draw pattern line at the bottom of the last page (works for first page too)
    this.drawPatternLine(doc);

    doc.save(filename);
  }

  // ============================================================================
  // MAP SEARCH PDF - Exact match to HTML
  // ============================================================================

  async generateMapSearchPdf(data: MapSearchPdfData, filename: string): Promise<void> {
    await this.preloadImages();
    const doc = new jsPDF('p', 'mm', 'letter');
    const x = this.px(28);
    const contentW = this.px(556);
    let y = 0;

    // ===== PAGE 1: Header, Description, Map, Legend, Filters, Analysis =====

    // Dark blue header (79px height)
    this.drawRect(doc, 0, 0, this.PAGE_WIDTH, this.px(79), this.colors.headerDark);

    // Logo text (placeholder - ideally would use actual logo image)
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text('RETAILSTAT', this.px(27), this.px(35));

    // Export info (right side of header)
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text('Exported On', this.PAGE_WIDTH - this.px(27), this.px(22), { align: 'right' });
    doc.text(data.exportDate, this.PAGE_WIDTH - this.px(27), this.px(32), { align: 'right' });
    doc.text(data.exportTime, this.PAGE_WIDTH - this.px(27), this.px(40), { align: 'right' });

    y = this.px(90);

    // User Description box (light blue with left border)
    const descBoxH = this.px(60);
    this.drawRect(doc, x, y, this.px(520), descBoxH, this.colors.descBg);
    this.drawRect(doc, x, y, 1.5, descBoxH, this.colors.descBorder);

    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...this.hexToRgb(this.colors.text));
    doc.text('User Description', x + 5, y + 8);

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...this.hexToRgb('#374151'));
    const descLines = doc.splitTextToSize(data.userDescription || 'No description provided', this.px(500));
    doc.text(descLines.slice(0, 2), x + 5, y + 16);

    y += descBoxH + 8;

    // Map image
    const mapH = this.px(200);
    if (data.mapImage) {
      try {
        doc.addImage(data.mapImage, 'PNG', x, y, this.px(561), mapH);
      } catch {
        this.drawRect(doc, x, y, this.px(561), mapH, '#f5f5f5');
        doc.setFontSize(10);
        doc.setTextColor(150, 150, 150);
        doc.text('Map image could not be loaded', x + this.px(280), y + mapH / 2, { align: 'center' });
      }
    } else {
      this.drawRect(doc, x, y, this.px(561), mapH, '#f5f5f5');
    }
    y += mapH + 5;

    // Store Legend
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...this.hexToRgb(this.colors.text));
    doc.text('Store Legend', x, y + 4);

    if (data.storeLegend.length > 0) {
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...this.hexToRgb('#000000B3'));
      const legendText = data.storeLegend.join('  •  ');
      const legendLines = doc.splitTextToSize(legendText, contentW - 30);
      doc.text(legendLines, x + 30, y + 4);
      y += 5 + legendLines.length * 4;
    } else {
      y += 8;
    }

    y += 5;

    // Active Filters box (if any)
    if (data.activeFilters.length > 0) {
      const filterBoxH = Math.min(this.px(92), 8 + data.activeFilters.length * 5);
      this.drawRect(doc, x, y, this.px(525), filterBoxH, '#FFFFFF', this.colors.filterBorder);

      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...this.hexToRgb(this.colors.text));
      doc.text('Active Filters', x + 4, y + 6);

      let filterY = y + 12;
      doc.setFontSize(8);
      data.activeFilters.slice(0, 5).forEach(f => {
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...this.hexToRgb(this.colors.text));
        doc.text(`${f.category}:`, x + 4, filterY);

        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...this.hexToRgb('#000000B3'));
        doc.text(` ${f.value}`, x + 4 + doc.getTextWidth(`${f.category}:`), filterY);
        filterY += 4;
      });

      y += filterBoxH + 5;
    }

    // Polygon Analysis box (if enabled)
    if (data.showPolygonAnalysis && data.polygonAnalysis) {
      const analysisBoxH = this.px(70);
      this.drawRect(doc, x, y, this.px(525), analysisBoxH, '#FFFFFF', this.colors.filterBorder);

      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...this.hexToRgb(this.colors.text));
      doc.text('POLYGON ANALYSIS', x + 4, y + 8);

      const metricY = y + 14;
      const metricW = this.px(150);

      // Area metric
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...this.hexToRgb(this.colors.text));
      doc.text('Area', x + 4, metricY);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...this.hexToRgb(this.colors.metricBlue));
      doc.text(data.polygonAnalysis.areaSquareFeet || '--', x + 4, metricY + 6);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...this.hexToRgb(this.colors.text));
      doc.text(data.polygonAnalysis.areaAcres || '', x + 4, metricY + 11);

      // Perimeter metric
      doc.setFontSize(8);
      doc.setTextColor(...this.hexToRgb(this.colors.text));
      doc.text('Perimeter', x + metricW, metricY);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...this.hexToRgb(this.colors.metricBlue));
      doc.text(data.polygonAnalysis.perimeterFeet || '--', x + metricW, metricY + 6);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...this.hexToRgb(this.colors.text));
      doc.text(data.polygonAnalysis.perimeterMiles || '', x + metricW, metricY + 11);

      // Center Point metric
      doc.setFontSize(8);
      doc.text('Center Point', x + metricW * 2, metricY);
      doc.setTextColor(...this.hexToRgb('#000000B3'));
      doc.text(data.polygonAnalysis.centerPointLat || '--', x + metricW * 2, metricY + 6);
      doc.text(data.polygonAnalysis.centerPointLong || '', x + metricW * 2, metricY + 11);

      y += analysisBoxH + 5;
    }

    // Page 1 Footer
    this.drawLine(doc, x, this.px(760), x + contentW, this.px(760), '#00000099', 0.3);
    doc.setFontSize(9);
    doc.setTextColor(...this.hexToRgb(this.colors.text));
    doc.text('retailstat.com', x, this.px(770));
    const totalPages = Math.max(2, Math.ceil(data.stores.length / 20) + 1);
    doc.text(`Page 1 of ${totalPages}`, x + contentW, this.px(770), { align: 'right' });

    // ===== PAGE 2+: Store Locations Table =====
    doc.addPage();

    // Table title with border
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...this.hexToRgb(this.colors.text));
    doc.text(`FILTERED LOCATIONS (${data.stores.length} STORES)`, this.px(34), this.px(40));
    this.drawLine(doc, this.px(34), this.px(48), this.px(34) + this.px(544), this.px(48), '#0000004D', 0.3);

    // Store table using autoTable
    const tableData = data.stores.map(s => {
      // Clean address (remove HTML tags)
      let address = s.address || '';
      address = address.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
      return [s.name || '', s.banner || '', s.fit || '', address];
    });

    autoTable(doc, {
      startY: this.px(55),
      head: [['Store Name', 'Banner', 'Format', 'Address']],
      body: tableData,
      theme: 'striped',
      styles: {
        fontSize: 9,
        cellPadding: 2,
        overflow: 'linebreak'
      },
      headStyles: {
        fillColor: [233, 233, 233],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        halign: 'center'
      },
      alternateRowStyles: { fillColor: [248, 248, 248] },
      columnStyles: {
        0: { cellWidth: this.px(177), halign: 'center' },
        1: { cellWidth: this.px(122), halign: 'center' },
        2: { cellWidth: this.px(122), halign: 'center' },
        3: { cellWidth: this.px(122), halign: 'center' }
      },
      margin: { left: this.px(34), right: this.margins.right },
      didDrawPage: (pageData) => {
        // Footer on each page
        this.drawLine(doc, this.px(34), this.px(760), this.px(34) + this.px(544), this.px(760), '#00000099', 0.3);
        doc.setFontSize(9);
        doc.setTextColor(...this.hexToRgb(this.colors.text));
        doc.text('retailstat.com', this.px(34), this.px(770));
        doc.text(`Page ${pageData.pageNumber} of ${totalPages}`, this.px(34) + this.px(544), this.px(770), { align: 'right' });
      }
    });

    doc.save(filename);
  }

  // ============================================================================
  // STORE ADD PDF - Pixel Perfect Match to HTML Template
  // ============================================================================

  async generateStoreAddPdf(data: StoreAddPdfData, filename: string): Promise<void> {
    await this.preloadImages();
    const doc = new jsPDF('p', 'mm', 'letter');

    // Page dimensions in mm (Letter: 215.9 x 279.4 mm)
    const pageW = this.PAGE_WIDTH; // 215.9mm

    // Convert px to mm (assuming 72 DPI for PDF)
    const pxToMm = (px: number) => px * 0.3528;

    // Key measurements from HTML (in px, converted to mm)
    const headerH = pxToMm(56); // 56px header height
    const patternH = pxToMm(15); // 15px pattern line height
    const contentX = pxToMm(34); // 34px left margin
    const contentW = pxToMm(544); // 544px content width

    let y = 0;

    // ===== PAGE 1: Header, Store Details, Summary, Map =====

    // Dark blue header (#0F1B4D) - 56px height
    this.drawRect(doc, 0, 0, pageW, headerH, '#0F1B4D');

    // Header content - Logo, Address, Date
    const headerContentY = headerH / 2 + 1;

    // RetailStat Logo (left side)
    if (this.imageAssets.retailstatLogo) {
      try {
        const logoW = pxToMm(123);
        const logoH = pxToMm(35);
        doc.addImage(this.imageAssets.retailstatLogo, 'PNG', pxToMm(11), (headerH - logoH) / 2, logoW, logoH);
      } catch {
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(255, 255, 255);
        doc.text('RETAILSTAT', pxToMm(11), headerContentY);
      }
    } else {
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(255, 255, 255);
      doc.text('RETAILSTAT', pxToMm(11), headerContentY);
    }

    // Address (center)
    doc.setFontSize(13);
    doc.setFont('SofiaSans-Bold', 'bold');
    doc.setTextColor(255, 255, 255);
    const locationText = data.location || 'Location not provided';
    doc.text(locationText, pageW / 2, headerContentY, { align: 'center' });

    // Generated Date (right side)
    doc.setFontSize(13);
    doc.setFont('SofiaSans-Bold', 'bold');
    const date = new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
    doc.text(`Generated: ${date}`, pageW - pxToMm(11), headerContentY, { align: 'right' });

    // Pattern line after header (two colored segments)
    y = headerH;
    this.drawPatternLine(doc, y);
    y += patternH;

    // Main content area starts at 15% from top as per HTML
    y = pxToMm(792 * 0.15); // 15% of page height

    // Page Title
    doc.setFontSize(22);
    doc.setFont('SofiaSans-Bold', 'bold');
    doc.setTextColor(...this.hexToRgb('#2D5D9B'));
    doc.text('Store Details entered to Project Sales', contentX, y);
    y += 13;

    // Store Information section
    doc.setFontSize(15);
    doc.setFont('SofiaSans-Regular', 'normal');
    doc.setTextColor(...this.hexToRgb('#000000'));
    doc.text("Grocer's Name :", contentX, y);
    doc.setFont('SofiaSans-Bold', 'bold');
    doc.text(` ${data.grocerName || '--'}`, contentX + doc.getTextWidth("Grocer's Name :"), y);
    y += 12;

    doc.setFont('SofiaSans-Regular', 'normal');
    doc.text('SQFT Value :', contentX, y);
    doc.setFont('SofiaSans-Bold', 'bold');
    doc.text(` ${data.sqft || '--'}`, contentX + doc.getTextWidth('SQFT Value :'), y);
    y += 5;

    if (data.sqftWarning) {
      doc.setFontSize(12);
      doc.setFont('SofiaSans-Regular', 'normal');
      doc.setTextColor(...this.hexToRgb('#6C6A61'));
      const warningLines = doc.splitTextToSize(data.sqftWarning, contentW);
      doc.text(warningLines.slice(0, 2), contentX, y + 3);
      y += warningLines.length * 3 + 4;
    }

    y += 8;

    // Summary Section header with border
    doc.setFontSize(16);
    doc.setFont('SofiaSans-Bold', 'bold');
    doc.setTextColor(...this.hexToRgb(this.colors.primary));
    doc.text('SUMMARY SECTION', contentX, y);
    this.drawLine(doc, contentX, y + 2, contentX + contentW, y + 2, this.colors.border, 0.3);
    y += 10;

    // Summary grid (2 rows x 3 columns) with borders matching HTML
    const cellW = pxToMm(180);
    let cellH = pxToMm(66);
    const gridW = pxToMm(538);

    // Check if labels need extra height (3+ lines)
    const totalSalesLines = data.totalSalesProjectionLabel ? doc.splitTextToSize(data.totalSalesProjectionLabel, cellW - 6).length : 0;
    const sqftLines = data.sqftProjectionLabel ? doc.splitTextToSize(data.sqftProjectionLabel, cellW - 6).length : 0;
    const maxLines = Math.max(totalSalesLines, sqftLines);

    if (maxLines >= 3) {
      cellH = pxToMm(80); // Increase height for 3+ lines
    }

    // Draw grid background
    this.drawRect(doc, contentX, y, gridW, cellH * 2, '#F8F8F8');

    // Row 1 cells
    // Cell 1: Projected Weekly Sales Range
    doc.setFontSize(10);
    doc.setFont('SofiaSans-Regular', 'normal');
    doc.setTextColor(...this.hexToRgb('#000000'));
    doc.text('Projected Weekly Sales Range (In $)', contentX + 3, y + 5);
    doc.setFontSize(12);
    doc.setFont('SofiaSans-Bold', 'bold');
    doc.text(data.weeklySalesRange || '--', contentX + 3, y + 12);

    // Cell 2: Total Sales Projection
    doc.setFontSize(10);
    doc.setFont('SofiaSans-Regular', 'normal');
    doc.text('Total Sales Projection', contentX + cellW + 3, y + 5);
    doc.setFontSize(12);
    doc.setFont('SofiaSans-Bold', 'bold');
    doc.text(data.totalSalesProjection || '--', contentX + cellW + 3, y + 10);
    if (data.totalSalesProjectionLabel) {
      doc.setFontSize(11);
      doc.setFont('SofiaSans-Regular', 'normal');
      this.drawMultiLineText(doc, data.totalSalesProjectionLabel, contentX + cellW + 3, y + 15, cellW - 6);
    }

    // Cell 3: Sales Per SQFT Projection
    doc.setFontSize(10);
    doc.setFont('SofiaSans-Regular', 'normal');
    doc.text('Sales Per SQFT Projection', contentX + cellW * 2 + 3, y + 5);
    doc.setFontSize(12);
    doc.setFont('SofiaSans-Bold', 'bold');
    doc.text(data.sqftProjection || '--', contentX + cellW * 2 + 3, y + 10);
    if (data.sqftProjectionLabel) {
      doc.setFontSize(11);
      doc.setFont('SofiaSans-Regular', 'normal');
      this.drawMultiLineText(doc, data.sqftProjectionLabel, contentX + cellW * 2 + 3, y + 15, cellW - 6);
    }

    // Draw cell borders for row 1
    this.drawLine(doc, contentX + cellW, y, contentX + cellW, y + cellH, 'rgba(0,0,0,0.8)', 0.3);
    this.drawLine(doc, contentX + cellW * 2, y, contentX + cellW * 2, y + cellH, 'rgba(0,0,0,0.8)', 0.3);
    this.drawLine(doc, contentX, y + cellH, contentX + gridW, y + cellH, 'rgba(0,0,0,0.8)', 0.3);

    y += cellH;

    // Row 2 cells
    // Cell 1: Closest Same Banner
    doc.setFontSize(10);
    doc.setFont('SofiaSans-Regular', 'normal');
    doc.setTextColor(...this.hexToRgb('#000000'));
    doc.text(`Closest ${data.grocerName || 'Store'}`, contentX + 3, y + 5);
    doc.setFontSize(12);
    doc.setFont('SofiaSans-Bold', 'bold');
    doc.text(data.closestSameBanner || '--', contentX + 3, y + 12);

    // Cell 2: Closest Competitor
    doc.setFontSize(10);
    doc.setFont('SofiaSans-Regular', 'normal');
    doc.text('Closest Competitor', contentX + cellW + 3, y + 5);
    doc.setFontSize(12);
    doc.setFont('SofiaSans-Bold', 'bold');
    const compText = data.closestCompetitor || '--';
    const compLines = doc.splitTextToSize(compText, cellW - 6);
    doc.text(compLines[0], contentX + cellW + 3, y + 12);
    if (compLines[1]) {
      doc.text(compLines[1], contentX + cellW + 3, y + 18);
    }

    // Cell 3: Future Openings
    doc.setFontSize(10);
    doc.setFont('SofiaSans-Regular', 'normal');
    doc.text('Future openings', contentX + cellW * 2 + 3, y + 5);
    doc.setFontSize(12);
    doc.setFont('SofiaSans-Bold', 'bold');
    doc.text('Count within 7 mins / closest', contentX + cellW * 2 + 3, y + 10);
    doc.setFontSize(12);
    doc.setFont('SofiaSans-Bold', 'bold');
    const futureText = data.futureOpenings || '--';
    const futureLines = doc.splitTextToSize(futureText, cellW - 6);
    // Draw all lines, not just the first one
    let futureY = y + 17;
    futureLines.forEach((line: string) => {
      doc.text(line, contentX + cellW * 2 + 3, futureY);
      futureY += 5;
    });

    // Draw cell borders for row 2
    this.drawLine(doc, contentX + cellW, y, contentX + cellW, y + cellH, 'rgba(0,0,0,0.8)', 0.3);
    this.drawLine(doc, contentX + cellW * 2, y, contentX + cellW * 2, y + cellH, 'rgba(0,0,0,0.8)', 0.3);

    y += cellH + 8;

    // Map placeholder/image with aspect ratio preservation
    const maxMapW = pxToMm(538);
    const fixedMapH = pxToMm(225);
    if (data.mapImage) {
      try {
        const imgProps = doc.getImageProperties(data.mapImage);
        const aspectRatio = imgProps.width / imgProps.height;
        const calculatedW = fixedMapH * aspectRatio;
        const finalW = Math.min(calculatedW, maxMapW);
        const centerX = contentX + (maxMapW - finalW) / 2;
        doc.addImage(data.mapImage, 'PNG', centerX, y, finalW, fixedMapH);
      } catch {
        this.drawRect(doc, contentX, y, maxMapW, fixedMapH, '#f5f5f5');
        doc.setFontSize(12);
        doc.setTextColor(150, 150, 150);
        doc.text('Map Snapshot', contentX + maxMapW / 2, y + fixedMapH / 2, { align: 'center' });
      }
    } else {
      this.drawRect(doc, contentX, y, maxMapW, fixedMapH, '#f5f5f5');
      doc.setFontSize(12);
      doc.setTextColor(150, 150, 150);
      doc.text('Map Snapshot', contentX + maxMapW / 2, y + fixedMapH / 2, { align: 'center' });
    }

    // Pattern line at bottom of page 1
    this.drawPatternLine(doc);

    // ===== PAGE 2+: Competitive Summary (Dynamic Pagination) =====
    // Constants matching HTML design
    const page2ContentX = pxToMm(35);
    const page2ContentW = pxToMm(544);
    const page2StartY = pxToMm(52);
    const ITEMS_PER_PAGE = 21;
    const rowH = pxToMm(30); // 30px row height from SCSS
    const headerRowH = pxToMm(25.795); // Header row height from SCSS

    // Column widths - adjust for better alignment
    const bannerColW = Math.floor(page2ContentW * 0.6);
    const countColW = pxToMm(80); // Fixed width for count column
    const salesColW = page2ContentW - bannerColW - countColW; // Remaining width for sales column

    // Helper function to draw competitive page header
    const drawCompetitiveHeader = (_pageNum: number, isFirstPage: boolean) => {
      const titleText = isFirstPage ? 'Competitive Summary' : 'Competitive Summary (Continued)';
      const estLabel = data.keyIndicator === 'SALES_VOLUME' ? 'Sales Volume (In $)' : 'Weekly Sales Per SQFT (In $)';
      const regionText = data.competitiveRegionText || data.regionText || '7 minutes Drive Time';

      // Title on left side
      doc.setFontSize(16);
      doc.setFont('SofiaSans-Bold', 'bold');
      doc.setTextColor(...this.hexToRgb(this.colors.primary));
      doc.text(titleText.toUpperCase(), page2ContentX, page2StartY);

      // Right side: "Est. [estLabel]    [regionText]"
      // Calculate positions from right to left to avoid overlap
      doc.setFontSize(12);
      doc.setFont('SofiaSans-Regular', 'normal');
      doc.setTextColor(...this.hexToRgb('#000000'));

      // Region text at far right
      const regionTextWidth = doc.getTextWidth(regionText);
      const regionX = page2ContentX + page2ContentW - regionTextWidth;
      doc.text(regionText, regionX, page2StartY);

      // Est. label and value before region text with proper gap
      const gap = pxToMm(25);
      const estText = 'Est.';
      const estTextWidth = doc.getTextWidth(estText);
      const estLabelWidth = doc.getTextWidth(estLabel);

      doc.setFont('SofiaSans-Regular', 'normal');
      doc.text(estText, regionX - gap - estLabelWidth - estTextWidth - 3, page2StartY);
      doc.setFont('SofiaSans-Regular', 'normal');
      doc.text(estLabel, regionX - gap - estLabelWidth - 2, page2StartY);

      // Header underline (0.885px solid #000 from SCSS)
      this.drawLine(doc, page2ContentX, page2StartY + 3, page2ContentX + page2ContentW, page2StartY + 3, '#000000', 0.3);
    };

    // Helper function to draw table header row
    const drawTableHeader = (startY: number) => {
      const columnHeader = data.keyIndicator === 'SALES_VOLUME' ? 'Sales Volume (In $)' : 'Weekly Sales Per SQFT (In $)';

      // Background for header with bottom border
      doc.setFillColor(248, 248, 248);
      doc.setDrawColor(102, 102, 102); // rgba(0,0,0,0.4)
      doc.setLineWidth(0.25);
      doc.rect(page2ContentX, startY, page2ContentW, headerRowH, 'F');
      // Draw bottom border line explicitly with stronger line
      doc.setDrawColor(0, 0, 0);
      doc.setLineWidth(0.8);
      doc.line(page2ContentX, startY + headerRowH, page2ContentX + page2ContentW, startY + headerRowH);

      // Banner header
      doc.setFontSize(12);
      doc.setFont('SofiaSans-Bold', 'bold');
      doc.setTextColor(0, 0, 0);
      doc.text('Banner', page2ContentX + pxToMm(12), startY + headerRowH / 2 + 1);

      // Sales header (centered in sales column)
      const salesColCenterX = page2ContentX + bannerColW + salesColW / 2 - pxToMm(40);
      doc.text(columnHeader, salesColCenterX, startY + headerRowH / 2 + 1, { align: 'center' });

      // Count header (right aligned)
      doc.text('Count', page2ContentX + page2ContentW - pxToMm(15), startY + headerRowH / 2 + 1, { align: 'right' });

      return startY + headerRowH;
    };

    // Helper function to draw a data row
    const drawTableRow = (item: any, startY: number) => {
      // Row background with stroke for bottom border
      doc.setFillColor(248, 248, 248);
      doc.setDrawColor(102, 102, 102); // rgba(0,0,0,0.4) - gray for border
      doc.setLineWidth(0.25);
      // Draw rect with fill and bottom stroke
      doc.rect(page2ContentX, startY, page2ContentW, rowH, 'F');
      // Draw bottom border line explicitly with stronger line
      doc.setDrawColor(0, 0, 0);
      doc.setLineWidth(0.8);
      doc.line(page2ContentX, startY + rowH, page2ContentX + page2ContentW, startY + rowH);

      // Banner text
      doc.setFontSize(12);
      doc.setFont('SofiaSans-Regular', 'normal');
      doc.setTextColor(0, 0, 0);
      const bannerText = String(item.banner || item.name || '');
      doc.text(bannerText, page2ContentX + pxToMm(12), startY + rowH / 2 + 1);

      // Value text (centered in the sales column) - adjusted slightly left for alignment
      const valueText = Number(item.value).toLocaleString('en-US');
      const salesColCenterX = page2ContentX + bannerColW + salesColW / 2 - pxToMm(5);
      doc.text(valueText, salesColCenterX, startY + rowH / 2 + 1, { align: 'center' });

      // Count text (right aligned)
      const countText = String(item.count || item.totalCount || '');
      doc.text(countText, page2ContentX + page2ContentW - pxToMm(15), startY + rowH / 2 + 1, { align: 'right' });

      return startY + rowH;
    };

    // Draw competitive pages
    let demographicHeaderAdded = false;
    let ethnicityPopulationAdded = false;
    let peopleIncomeAdded = false;

    if (data.competitiveData?.length > 0) {
      const totalItems = data.competitiveData.length;
      const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);

      for (let pageNum = 0; pageNum < totalPages; pageNum++) {
        doc.addPage();

        // Draw header for this page
        drawCompetitiveHeader(pageNum, pageNum === 0);

        // Draw table
        let tableY = page2StartY + pxToMm(10); // Gap after header

        // Draw table header
        tableY = drawTableHeader(tableY);

        // Draw data rows for this page
        const startIndex = pageNum * ITEMS_PER_PAGE;
        const endIndex = Math.min(startIndex + ITEMS_PER_PAGE, totalItems);
        const pageItems = data.competitiveData.slice(startIndex, endIndex);

        pageItems.forEach((item) => {
          tableY = drawTableRow(item, tableY);
        });

        // Check if there's space for demographics on the last competitive page
        const remainingSpace = this.PAGE_HEIGHT - pxToMm(15) - tableY; // Space minus pattern line
        const demographicHeaderHeight = pxToMm(80); // Approximate header height
        const ethnicityPopulationHeight = pxToMm(120); // Approximate section height
        const peopleIncomeHeight = pxToMm(100); // Approximate people/income height
        const logoHeight = pxToMm(40); // Logo space needed (reduced)
        const fullDemographicWithLogoHeight = demographicHeaderHeight + ethnicityPopulationHeight + peopleIncomeHeight + logoHeight;
        //const headerEthnicityOnlyHeight = demographicHeaderHeight + ethnicityPopulationHeight + pxToMm(40); // Conservative padding

        const rowsOnThisPage = Math.min(ITEMS_PER_PAGE, totalItems - (pageNum * ITEMS_PER_PAGE));

        if (pageNum === totalPages - 1 && data.demographics && rowsOnThisPage <= 13) {
          if (rowsOnThisPage <= 3 && remainingSpace >= fullDemographicWithLogoHeight + pxToMm(30)) {
            // Only fit full demographic profile with logo if less than 3 rows (reduced spacing)
            tableY += 15;
            tableY = this.drawDemographicHeader(doc, data.demographics, tableY);
            tableY = this.drawEthnicityAndPopulation(doc, data.demographics, tableY);
            tableY = this.drawPeopleAndIncome(doc, data.demographics, tableY);
            this.drawLastPageFooter(doc, tableY + 3); // Reduced spacing for < 3 rows
            demographicHeaderAdded = true;
            ethnicityPopulationAdded = true;
            peopleIncomeAdded = true;
          } else if (remainingSpace >= pxToMm(100)) {
            // For > 3 rows, always split: header + ethnicity/population here, people/income + logo on next page
            tableY += 15;
            tableY = this.drawDemographicHeader(doc, data.demographics, tableY);
            this.drawEthnicityAndPopulation(doc, data.demographics, tableY);
            demographicHeaderAdded = true;
            ethnicityPopulationAdded = true;
          }
        }

        // Draw pattern line at bottom of each competitive page
        this.drawPatternLine(doc);
      }
    } else {
      // No competitive data - still add a page with message
      doc.addPage();
      drawCompetitiveHeader(0, true);
      doc.setFontSize(10);
      doc.setTextColor(...this.hexToRgb('#666666'));
      doc.text('No competitive data available', page2ContentX, page2StartY + 20);

      // Try to fit demographics after no data message
      let currentY = page2StartY + 40;
      const remainingSpace = this.PAGE_HEIGHT - pxToMm(15) - currentY;
      const demographicHeaderHeight = pxToMm(80);
      const ethnicityPopulationHeight = pxToMm(120);
      const peopleIncomeHeight = pxToMm(100);
      const logoHeight = pxToMm(50);

      if (data.demographics) {
        const fullDemographicWithLogoHeight = demographicHeaderHeight + ethnicityPopulationHeight + peopleIncomeHeight + logoHeight;

        if (remainingSpace >= fullDemographicWithLogoHeight) {
          currentY = this.drawDemographicHeader(doc, data.demographics, currentY);
          currentY = this.drawEthnicityAndPopulation(doc, data.demographics, currentY);
          currentY = this.drawPeopleAndIncome(doc, data.demographics, currentY);
          this.drawLastPageFooter(doc, currentY + 10);
          demographicHeaderAdded = true;
          ethnicityPopulationAdded = true;
          peopleIncomeAdded = true;
        } else if (remainingSpace >= demographicHeaderHeight + ethnicityPopulationHeight) {
          currentY = this.drawDemographicHeader(doc, data.demographics, currentY);
          this.drawEthnicityAndPopulation(doc, data.demographics, currentY);
          demographicHeaderAdded = true;
          ethnicityPopulationAdded = true;
        }
      }

      this.drawPatternLine(doc);
    }

    // ===== PAGE 3: Demographics (only remaining sections) =====
    if (data.demographics && (!demographicHeaderAdded || !ethnicityPopulationAdded || !peopleIncomeAdded)) {
      doc.addPage();
      y = pxToMm(55);

      // Only add sections that weren't already added
      if (!demographicHeaderAdded) {
        y = this.drawDemographicHeader(doc, data.demographics, y);
      }
      if (!ethnicityPopulationAdded) {
        y = this.drawEthnicityAndPopulation(doc, data.demographics, y);
      }
      if (!peopleIncomeAdded) {
        y = this.drawPeopleAndIncome(doc, data.demographics, y);
      }

      this.drawLastPageFooter(doc, y + 20);
      this.drawPatternLine(doc);
    }

    doc.save(filename);
  }

  // ============================================================================
  // RANKING TABLE PDF
  // ============================================================================


async generateRankingTablePdf(data: RankingTablePdfData, filename: string): Promise<void> {
  await this.preloadImages();

  // Portrait letter — keep existing page dimensions & margins
  const doc = new jsPDF('p', 'mm', 'letter');
  const pageW  = this.PAGE_WIDTH;   // 215.9
  const marginL = this.margins.left; // 6
  const marginR = this.margins.right;// 7
  const contentW = pageW - marginL - marginR; // 202.9

  // ── HEADER BAR ──────────────────────────────────────────────────────────
  // Matches Figma: dark navy #0F1B4D, full-width bar
  const headerH = this.px(46); // 46px from Figma → mm
  this.drawRect(doc, 0, 0, pageW, headerH, '#0F1B4D');

  // Logo — left side (Figma: x=28, width=93, height=28 px)
  const logoW = this.px(93);
  const logoH = this.px(28);
  const logoX = this.px(28);
  const logoY = (headerH - logoH) / 2;
  if (this.imageAssets.retailstatLogo) {
    try {
      doc.addImage(this.imageAssets.retailstatLogo, 'PNG', logoX, logoY, logoW, logoH);
    } catch {
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(255, 255, 255);
      doc.text('RETAILSTAT', logoX, headerH / 2 + 2);
    }
  } else {
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text('RETAILSTAT', logoX, headerH / 2 + 2);
  }

  // Center title — "{storeName}'s Store Ranking (Sales Volume)"
  const rankingTitle = `${data.storeName || 'Store'}'s Store Ranking (Sales Volume)`;
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(255, 255, 255);
  doc.text(rankingTitle, pageW / 2, headerH / 2 + 2, { align: 'center' });

  // Right — "Generated: MM-DD-YYYY"
  const today = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  const dateStr = `Generated: ${pad(today.getMonth() + 1)}-${pad(today.getDate())}-${today.getFullYear()}`;
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.text(dateStr, pageW - marginR, headerH / 2 + 2, { align: 'right' });

  // ── INFO SECTION ────────────────────────────────────────────────────────
  let y = headerH + this.px(20);
  const leftX  = marginL;
  const rightX = marginL + contentW * 0.55;      // right column at 55%
  const leftColMaxWidth = contentW * 0.52;        // max text width for left column values

  // ── RIGHT COLUMN: draw at fixed positions independently ──────────────────
  let rightY = y;
  const rightLineH = 5.5;

  const drawRightItem = (label: string, value: string): void => {
    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...this.hexToRgb(this.colors.text));
    doc.text(label, rightX, rightY);
    doc.setFont('helvetica', 'bold');
    doc.text(value, rightX + doc.getTextWidth(label), rightY);
    rightY += rightLineH;
  };

  drawRightItem('Est. Sales Volume: ', data.estSalesVolume || 'Not Available');
  drawRightItem('Est. Sales/SQFT: ', data.estSalesSqft   || 'Not Available');
  drawRightItem('FIT Category: ',    data.fitCategory    || 'N/A');
  drawRightItem('Status: ',          data.storeStatus    || 'N/A');

  // ── LEFT COLUMN: wrappable values, y advances per item ───────────────────
  const lineH     = 4.8;
  const infoFontSize = 8.5;

  const drawLeftItem = (label: string, value: string): void => {
    doc.setFontSize(infoFontSize);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...this.hexToRgb(this.colors.text));
    const labelText = `${label}: `;
    const labelW    = doc.getTextWidth(labelText);
    doc.text(labelText, leftX, y);
    doc.setFont('helvetica', 'bold');
    const maxValW   = leftColMaxWidth - labelW;
    const lines     = doc.splitTextToSize(value, maxValW);
    doc.text(lines, leftX + labelW, y);
    y += lineH * lines.length;
  };

  // Store Address (may wrap for long addresses)
  drawLeftItem('Store Address', data.storeAddress || 'N/A');
  y += 2;

  // Filters Applied heading
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...this.hexToRgb(this.colors.text));
  doc.text('Filters Applied', leftX, y);
  y += lineH + 1;

  // Filter rows (States + CBSA replaces single Geography; Landlord removed)
  [
    { label: 'Sales Volume', value: data.filters?.salesVolume || 'Not Applied' },
    { label: 'States',       value: data.filters?.states      || 'Not Applied' },
    { label: 'CBSA',         value: data.filters?.cbsa        || 'Not Applied' },
    { label: 'FIT',          value: data.filters?.fit         || 'Not Applied' },
    { label: 'SQFT',         value: data.filters?.sqft        || 'Not Applied' },
  ].forEach(f => drawLeftItem(f.label, f.value));

  // Make sure y clears both columns before the table
  y = Math.max(y, rightY) + 5;

  // ── TABLE SECTION HEADING ────────────────────────────────────────────────
  // Matches Figma: "Store Sales and Rankings Summary" in bold, black
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...this.hexToRgb(this.colors.text));
  doc.text('Store Sales and Rankings Summary', marginL, y);
  y += 4;

  const milesLabel = `${data.milesOption || '3'} Miles`;
  const minutesLabel = `${data.minutesOption || '5'} Min`;

  // Type sizes for the ranking table. Kept as constants because the Location Name
  // column is drawn by hand (name + address sub-line) and its line-height maths must
  // stay in lockstep with what autoTable renders, or the address overlaps the name.
  const TABLE_FONT_SIZE = 8;
  const NAME_FONT_SIZE  = 8;
  const ADDR_FONT_SIZE  = 7;

  // Column proportions in Figma px. 12 columns: Store # (GRO-820) plus Store FIT +
  // Store Size + Distance (GRO-711). The first 11 are computed proportionally; the last
  // column (Distance) absorbs the rounding remainder so the widths sum to contentW
  // exactly — this is what prevents phantom empty columns.
  // Every column except Location Name is sized to hold its widest realistic token on
  // one line at TABLE_FONT_SIZE, with ~0.5mm to spare — a token broken mid-word reads
  // as a typo ("Conventional" → "Convention"/"al"). Measured worst cases, mm of text:
  //   Store #     "123456"       9.40     Store FIT   "Conventional"  16.34
  //   Sales       "$999.99M"    12.45     Store Size  "180,000"       10.10
  //   NATL Rank   "1234/5678"   13.21     Distance    "1,450.7234"    14.00
  //   Other ranks "123/456"     10.10
  // Distance keeps the API's own precision, so it is sized for four decimals; a longer
  // value is caught by fitTokenFontSize rather than being split across lines.
  // Miles/Minutes are deliberately narrower: they rank same-banner stores inside a
  // radius, so their totals stay two digits ("9/13"), unlike the national counts.
  // Store # is sized off the bold header token "Store" (6.9mm) and a six-digit number,
  // and its 48px come out of Location Name rather than off the whole table, so every
  // other column keeps the clearance measured above.
  // Location Name absorbs the remainder — store names are the only genuinely long
  // values, and anything left over goes to fitting more of them on one line.
  const totalFigmaPx = 706;
  const figmaCols = [111, 48, 59, 63, 51, 51, 51, 41, 41, 74, 51];
  const fixedColWidths = figmaCols.map(px =>
    parseFloat(((px / totalFigmaPx) * contentW).toFixed(2))
  );
  const lastColW = parseFloat(
    (contentW - fixedColWidths.reduce((a, b) => a + b, 0)).toFixed(2)
  );
  // Per-column text alignment. autoTable only applies columnStyles to the BODY section
  // (`colStyles = sectionName === 'body' ? columnStyles : {}`), so head cells would all
  // inherit headStyles' right-align and sit out of line with left-aligned columns like
  // Store FIT. didParseCell below re-applies these to the header.
  const COL_ALIGN: ('left' | 'right')[] = [
    'left',   // Location Name
    'left',   // Store #
    'right',  // Annual/Weekly Sales
    'right',  // NATL Rank
    'right',  // State Rank
    'right',  // County Rank
    'right',  // MSA Rank
    'right',  // Miles
    'right',  // Minutes
    'left',   // Store FIT Category
    'right',  // Store Size
    'right',  // Distance
  ];

  // Location Name uses 4mm left / 1mm right padding (every other column uses 2mm, so
  // 12 columns fit on portrait letter). The tight right edge closes up the gap to the
  // sales column and buys the name a little more room.
  const COL0_PAD_L = 4;
  const COL0_PAD_R = 1;
  const col0W      = fixedColWidths[0];
  const col0InnerW = col0W - COL0_PAD_L - COL0_PAD_R;

  // Safety net for columns 1+: autoTable's 'linebreak' overflow splits a word in the
  // middle when it doesn't fit ("Conventional" → "Convention" / "al"). The widths above
  // are sized so that shouldn't happen, but an unusually long FIT name or an outsized
  // rank would still hit it — so measure the widest token per cell and shrink just that
  // cell's font enough to keep it whole. Column 0 is excluded: it draws its own
  // name + address text and wrapping long store names there is intended.
  const allColWidths = [...fixedColWidths, lastColW];
  const MIN_CELL_FONT_SIZE = 6;
  const fitTokenFontSize = (text: string, colIndex: number, bold: boolean): number => {
    const innerW = allColWidths[colIndex] - 4; // 2mm side padding
    doc.setFont('helvetica', bold ? 'bold' : 'normal');
    doc.setFontSize(TABLE_FONT_SIZE);
    const widestToken = text.split(/\s+/)
      .reduce((widest, token) => Math.max(widest, doc.getTextWidth(token)), 0);
    if (widestToken <= innerW || widestToken === 0) {
      return TABLE_FONT_SIZE;
    }
    // Text width scales linearly with font size; floor to 0.1pt and never go unreadable
    const scaled = Math.floor((TABLE_FONT_SIZE * innerW / widestToken) * 10) / 10;
    return Math.max(MIN_CELL_FONT_SIZE, scaled);
  };

  // Line-height constants (fontSize × pt→mm × leading)
  const nameLH = NAME_FONT_SIZE * 0.3528 * 1.15;
  const addrLH = ADDR_FONT_SIZE * 0.3528 * 1.15;
  // Gap between the last name-line height and the address baseline.
  // Must match the constant used in didDrawCell.
  const NAME_ADDR_GAP = 3; // mm

  const salesColumnLabel = data.salesType || 'Annual Sales';

  const tableHead = [[
    'Location Name',
    'Store #',
    salesColumnLabel,
    'NATL Rank',
    'State Rank',
    'County Rank',
    'MSA Rank',
    milesLabel,
    minutesLabel,
    'Store FIT',
    data.storeSizeLabel ? `Store Size (${data.storeSizeLabel})` : 'Store Size',
    'Distance (Miles)',
  ]];

  const tableRows = data.tableRows.map(row => [
    row.name,
    row.storeNumber ?? '',
    row.annualSales,
    row.nationalRank,
    row.stateRank,
    row.countyRank,
    row.msaRank,
    row.miles,
    row.minutes,
    row.fit ?? 'N/A',
    row.storeSize ?? 'N/A',
    row.distance ?? 'N/A',
  ]);

  autoTable(doc, {
    head: tableHead,
    body: tableRows,
    startY: y,
    // top: push continuation-page rows below the repeated header bar
    // bottom: reserve space for the pattern line (≈5.3mm) + buffer
    margin: { left: marginL, right: marginR, top: headerH + 3, bottom: 10 },
    tableWidth: contentW,
    // Never split a single row across two pages — the entire row moves to the
    // next page if it doesn't fit. This eliminates phantom empty columns and
    // address text overflowing the cell on page breaks.
    rowPageBreak: 'avoid',
    styles: {
      font: 'helvetica',
      fontSize: TABLE_FONT_SIZE,
      // 2mm side padding keeps 12 columns legible on portrait letter; Location Name
      // gets its own padding in columnStyles below.
      cellPadding: { top: 2, bottom: 2, left: 2, right: 2 },
      textColor: [0, 0, 0],
      lineColor: [180, 180, 180],
      lineWidth: 0,
      overflow: 'linebreak',
      minCellHeight: 8,
    },
    headStyles: {
      fillColor: [233, 233, 233],
      textColor: [50, 50, 50],
      fontStyle: 'bold',
      fontSize: TABLE_FONT_SIZE,
      halign: 'right',
      valign: 'middle',
      cellPadding: { top: 4, bottom: 4, left: 2, right: 2 },
      lineWidth: { bottom: 0.4 },
      lineColor: [180, 180, 180],
    },
    // Widths and alignment come from allColWidths / COL_ALIGN so the header (aligned in
    // didParseCell) and the body can never drift apart. Column 0 keeps its own padding —
    // col0InnerW and didDrawCell assume exactly this.
    columnStyles: allColWidths.reduce((styles, cellWidth, index) => {
      styles[index] = { halign: COL_ALIGN[index], cellWidth };
      if (index === 0) {
        styles[index].cellPadding = { top: 2, bottom: 2, left: COL0_PAD_L, right: COL0_PAD_R };
      }
      return styles;
    }, {} as Record<number, any>),
    alternateRowStyles: { fillColor: [248, 248, 248] },
    didParseCell: (hookData) => {
      // Head cells never receive columnStyles (see COL_ALIGN above), so align them
      // with the data underneath by hand
      if (hookData.section === 'head') {
        hookData.cell.styles.halign = COL_ALIGN[hookData.column.index] ?? 'right';
      }

      // Keep every token in the narrow columns whole (see fitTokenFontSize above)
      if (hookData.column.index > 0) {
        const cellText = Array.isArray(hookData.cell.text)
          ? hookData.cell.text.join(' ')
          : String(hookData.cell.text ?? '');
        const fitted = fitTokenFontSize(
          cellText, hookData.column.index, hookData.section === 'head'
        );
        if (fitted < TABLE_FONT_SIZE) {
          hookData.cell.styles.fontSize = fitted;
        }
      }

      if (hookData.section === 'body') {
        const raw = data.tableRows[hookData.row.index];
        if (!raw) return;

        // Highlight entire selected-store row
        if (raw.isSelected) {
          hookData.cell.styles.fillColor = [235, 245, 255];
        }

        // Location Name column — set display text and pre-compute required height
        if (hookData.column.index === 0) {
          const displayName = raw.isSelected ? `${raw.name} (Selected Store)` : raw.name;
          hookData.cell.text = [displayName];
          if (raw.isSelected) {
            hookData.cell.styles.fontStyle = 'bold';
          }

          if (raw.address) {
            // Count lines that the name and address will each occupy at render time.
            // Uses the same col0InnerW and gap constant as didDrawCell so the
            // minCellHeight is always tall enough to contain both.
            doc.setFontSize(NAME_FONT_SIZE);
            const nameLineCount = doc.splitTextToSize(displayName, col0InnerW).length;
            doc.setFontSize(ADDR_FONT_SIZE);
            const addrLineCount = doc.splitTextToSize(raw.address, col0InnerW).length;
            const paddingVert   = 2 + 2; // top + bottom cell padding
            const requiredH =
              paddingVert +
              nameLineCount * nameLH +
              NAME_ADDR_GAP +
              addrLineCount * addrLH;
            hookData.cell.styles.minCellHeight = Math.ceil(requiredH);
          }
        }
      }
    },
    didDrawCell: (hookData) => {
      if (hookData.section === 'body') {
        // Row separator drawn after the fill so it is never painted over
        const lineY = hookData.cell.y + hookData.cell.height;
        doc.setDrawColor(200, 200, 200);
        doc.setLineWidth(0.2);
        doc.line(hookData.cell.x, lineY, hookData.cell.x + hookData.cell.width, lineY);

        // Address sub-text rendered below the store name in the Location column
        if (hookData.column.index === 0) {
          const raw = data.tableRows[hookData.row.index];
          if (!raw?.address) return;

          const colInnerW = hookData.cell.width
            - hookData.cell.padding('left')
            - hookData.cell.padding('right');

          // Measure how many lines the name occupies so the address baseline
          // is always placed below the last name line without overlap.
          const displayName = raw.isSelected ? `${raw.name} (Selected Store)` : raw.name;
          doc.setFontSize(NAME_FONT_SIZE);
          doc.setFont('helvetica', raw.isSelected ? 'bold' : 'normal');
          const nameLines   = doc.splitTextToSize(displayName, colInnerW);
          const nameTotalH  = nameLines.length * nameLH;

          const addrY =
            hookData.cell.y +
            hookData.cell.padding('top') +
            nameTotalH +
            NAME_ADDR_GAP; // same constant used in didParseCell

          doc.setFontSize(ADDR_FONT_SIZE);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(108, 106, 97);
          doc.text(raw.address, hookData.cell.x + hookData.cell.padding('left'), addrY, {
            maxWidth: colInnerW,
          });
          // Reset text colour for subsequent cells
          doc.setTextColor(0, 0, 0);
        }
      }
    },
    didDrawPage: () => {
      // Redraw the header bar and pattern line on every continuation page
      this.drawRect(doc, 0, 0, pageW, headerH, '#0F1B4D');
      if (this.imageAssets.retailstatLogo) {
        try {
          doc.addImage(this.imageAssets.retailstatLogo, 'PNG', logoX, logoY, logoW, logoH);
        } catch { /* ignore */ }
      }
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(255, 255, 255);
      doc.text(rankingTitle, pageW / 2, headerH / 2 + 2, { align: 'center' });
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text(dateStr, pageW - marginR, headerH / 2 + 2, { align: 'right' });
      this.drawPatternLine(doc);
    },
  });

  // ── SMART END-PAGE FOOTER ────────────────────────────────────────────────
  // drawLastPageFooter(doc, contentEndY) internally does: footerY = contentEndY + 15
  // then draws: logo at footerY (20mm tall) and text at footerY + 25.
  // Total footer block height from footerY = 25 + ~5 (text) ≈ 30mm.
  const FOOTER_BLOCK_H = 30;   // mm: logo + gap + text, measured from footerStartY
  const DEFAULT_GAP    = 15;   // mm: preferred gap between table end and footer start
  const MIN_GAP        = 2;    // mm: smallest acceptable gap before we skip the footer

  const patternH = 15 * 0.3528;                                       // ≈ 5.3 mm
  const pageBottom = this.PAGE_HEIGHT - patternH;                     // usable bottom
  const finalY     = (doc as any).lastAutoTable?.finalY ?? pageBottom; // where table ended

  const available = pageBottom - finalY;                               // space left on page

  if (available >= MIN_GAP + FOOTER_BLOCK_H) {
    // Enough room — use the default gap, or shrink it to fit if space is between min and default
    const gap = Math.min(DEFAULT_GAP, available - FOOTER_BLOCK_H);
    // drawLastPageFooter adds 15 internally, so back-compute contentEndY
    this.drawLastPageFooter(doc, finalY + gap - 15);
  }
  // else: footer won't fit even at minimum gap — skip it, no new page created

  doc.save(filename);
}

// ============================================================================
// SCATTER CHART PDF
// ============================================================================

async generateScatterChartPdf(data: ScatterChartPdfData, filename: string): Promise<void> {
  await this.preloadImages();
  const doc = new jsPDF('p', 'mm', 'letter');
  const pageW = this.PAGE_WIDTH;
  const marginL = this.margins.left;
  const marginR = this.margins.right;
  const contentW = pageW - marginL - marginR;

  // Header bar
  const headerH = this.px(46);
  this.drawRect(doc, 0, 0, pageW, headerH, '#0F1B4D');

  // Logo
  const logoW = this.px(93);
  const logoH = this.px(28);
  const logoX = this.px(28);
  const logoY = (headerH - logoH) / 2;
  if (this.imageAssets.retailstatLogo) {
    try {
      doc.addImage(this.imageAssets.retailstatLogo, 'PNG', logoX, logoY, logoW, logoH);
    } catch {
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(255, 255, 255);
      doc.text('RETAILSTAT', logoX, headerH / 2 + 2);
    }
  }

  // Title - Store Name (Key Indicator)
  const headerTitle = `${data.storeName} (${data.keyIndicator})`;
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(255, 255, 255);
  doc.text(headerTitle, pageW / 2, headerH / 2 + 2, { align: 'center' });

  // Date
  const today = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  const dateStr = `Generated: ${pad(today.getMonth() + 1)}-${pad(today.getDate())}-${today.getFullYear()}`;
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.text(dateStr, pageW - marginR, headerH / 2 + 2, { align: 'right' });

  // Info section with two columns
  let y = headerH + this.px(20);
  const leftX = marginL;
  const rightX = marginL + contentW * 0.55;
  const lineH = 5.5;

  // ── LEFT COLUMN – Store info ────────────────────────────────────────────
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...this.hexToRgb(this.colors.text));
  doc.text('STORE INFORMATION', leftX, y);
  y += 6;

  const drawLeftItem = (label: string, value: string): void => {
    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...this.hexToRgb(this.colors.text));
    const labelText = `${label}: `;
    doc.text(labelText, leftX, y);
    doc.setFont('helvetica', 'bold');
    const maxValW = contentW * 0.52 - doc.getTextWidth(labelText);
    const lines = doc.splitTextToSize(value, maxValW);
    doc.text(lines, leftX + doc.getTextWidth(labelText), y);
    y += lineH * lines.length;
  };

  drawLeftItem('Store Address',    data.storeAddress    || 'N/A');
  drawLeftItem('Status',           data.storeStatus     || 'N/A');
  drawLeftItem('Est. Sales Volume', data.estSalesVolume || 'Not Available');
  drawLeftItem('Est. Sales/SQFT',  data.estSalesSqft   || 'Not Available');
  drawLeftItem('FIT Category',     data.fitCategory    || 'N/A');

  // ── RIGHT COLUMN – Options Selected ────────────────────────────────────
  let rightY = headerH + this.px(20);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...this.hexToRgb(this.colors.text));
  doc.text('Options Selected', rightX, rightY);
  rightY += 6;

  const drawRightLine = (label: string, value: string): void => {
    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...this.hexToRgb(this.colors.text));
    doc.text(`${label}: `, rightX, rightY);
    doc.setFont('helvetica', 'bold');
    doc.text(value, rightX + doc.getTextWidth(`${label}: `), rightY);
    rightY += lineH;
  };

  if (data.filters) {
    if (data.filters.storeType) {
      drawRightLine('Data', data.filters.storeType);
    }
    if (data.filters.region) {
      drawRightLine('Region', data.filters.region);
    }
    // Show Miles or Minutes depending on which filter is active
    if (data.filters.miles) {
      drawRightLine('Miles', data.filters.miles);
    } else if (data.filters.minutes) {
      drawRightLine('Minutes', data.filters.minutes);
    }
  }
  // if (data.keyIndicator) {
  //   doc.setFontSize(8.5);
  //   doc.setFont('helvetica', 'bold');
  //   doc.setTextColor(...this.hexToRgb(this.colors.text));
  //   doc.text(data.keyIndicator, rightX, rightY);
  //   rightY += lineH;
  // }

  // Move to next section
  y = Math.max(y, rightY) + 8;

  // Table heading
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...this.hexToRgb(this.colors.text));
  doc.text('Store Ranking Data', marginL, y);
  y += 4;

  // Table
  const tableHead = [['Rank', 'Location Name', 'Fit', 'Status', 'Sales Per SQFT', 'Total Area', 'Total Sales Volume']];
  const tableRows = data.tableRows.map(row => [
    String(row.rank),
    row.locationName,
    this.formatFitCategory(row.fit), // Format FIT category
    row.status,
    row.salesPerSqft,
    row.totalArea,
    row.totalSalesVolume
  ]);

  autoTable(doc, {
    head: tableHead,
    body: tableRows,
    startY: y,
    margin: { left: marginL, right: marginR, top: headerH + 3, bottom: 10 },
    tableWidth: contentW,
    rowPageBreak: 'avoid',
    styles: {
      font: 'helvetica',
      fontSize: 9,
      cellPadding: { top: 2, bottom: 2, left: 4, right: 4 },
      textColor: [0, 0, 0],
      lineColor: [180, 180, 180],
      lineWidth: 0,
      overflow: 'linebreak',
      minCellHeight: 8,
    },
    headStyles: {
      fillColor: [233, 233, 233],
      textColor: [50, 50, 50],
      fontStyle: 'bold',
      fontSize: 9,
      halign: 'center',
      valign: 'middle',
      cellPadding: { top: 4, bottom: 4, left: 4, right: 4 },
      lineWidth: { bottom: 0.4 },
      lineColor: [180, 180, 180],
    },
    columnStyles: {
      0: { halign: 'center', cellWidth: contentW * 0.08 },
      1: { halign: 'left', cellWidth: contentW * 0.24 },
      2: { halign: 'left', cellWidth: contentW * 0.14 },
      3: { halign: 'center', cellWidth: contentW * 0.10 },
      4: { halign: 'right', cellWidth: contentW * 0.15 },
      5: { halign: 'right', cellWidth: contentW * 0.14 },
      6: { halign: 'right', cellWidth: contentW * 0.15 },
    },
    alternateRowStyles: { fillColor: [248, 248, 248] },
    didParseCell: (hookData) => {
      if (hookData.section === 'body') {
        const raw = data.tableRows[hookData.row.index];
        if (raw?.isSelected) {
          hookData.cell.styles.fillColor = [235, 245, 255];
          hookData.cell.styles.fontStyle = 'bold';
        }
      }
    },
    didDrawPage: () => {
      this.drawRect(doc, 0, 0, pageW, headerH, '#0F1B4D');
      if (this.imageAssets.retailstatLogo) {
        try {
          doc.addImage(this.imageAssets.retailstatLogo, 'PNG', logoX, logoY, logoW, logoH);
        } catch { /* ignore */ }
      }
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(255, 255, 255);
      doc.text(headerTitle, pageW / 2, headerH / 2 + 2, { align: 'center' });
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text(dateStr, pageW - marginR, headerH / 2 + 2, { align: 'right' });
      this.drawPatternLine(doc);
    },
  });

  this.drawPatternLine(doc);
  doc.save(filename);
}
}
